import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CalendarClock,
  Calculator,
  Camera,
  Check,
  ChevronDown,
  CreditCard,
  FileText,
  GraduationCap,
  History,
  Image as ImageIcon,
  Loader2,
  Percent,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
  UserRound,
  Users,
  X
} from 'lucide-react';
import { api } from '../../services/api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useCaisse } from '../../contexts/CaisseContext';
import { useHabilitationCaisse } from '../../hooks/useHabilitationCaisse';
import BandeauCaisseReservee from '../caisse/BandeauCaisseReservee';
import { Toast } from '../ui/Toast';
import CarteChoix from '../ui/CarteChoix';
import CalculProrata from '../ui/CalculProrata';
import { LongToast, shouldUseLongToast } from '../ui/LongToast';
import ClassCapacityInfo from '../classes/ClassCapacityInfo';

const parentRelationOptions = [
  { value: 'pere', label: 'Père' },
  { value: 'mere', label: 'Mère' },
  { value: 'tuteur', label: 'Tuteur' },
  { value: 'autre', label: 'Autre responsable' }
];

const sexOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' }
];

const politiqueTypeOptions = [
  { value: 'admis_examen', label: 'Admis aux examens' },
  { value: 'premier_classe', label: 'Premier de classe' },
  { value: 'premiers_inscrits', label: 'Premiers inscrits' }
];

const defaultParent = {
  relation: 'tuteur',
  nom_prenom: '',
  email: '',
  telephones: [''],
  adresse: '',
  principal: true
};

const normalizeArrayResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const formatAmount = (value) => `${new Intl.NumberFormat('fr-FR').format(Number(value || 0))} FCFA`;


// Le jour d'un horodatage du calendrier, ramene a « AAAA-MM-JJ ».
//
// `mois_scolaires.date_debut` vaut minuit dans le fuseau de l'ecole et arrive
// ici en UTC (« 2025-09-30T22:00:00Z » pour le 1er octobre) : couper la chaine
// donnerait la veille, et l'ecran proposerait un prorata a un eleve present des
// le premier jour. On arrondit au jour le plus proche, ce qui reste juste quel
// que soit le fuseau du navigateur.
const jourCalendrier = (valeur) => {
  if (!valeur) return null;
  const texte = String(valeur);
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return texte;
  const instant = new Date(texte);
  if (Number.isNaN(instant.getTime())) return null;
  return new Date(instant.getTime() + 12 * 3600 * 1000).toISOString().slice(0, 10);
};

const toMoneyNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number)) : 0;
};

// Ce que le mode « historique » reprend d'un membre de la famille source : les
// montants `reduction_*_reprise` calculés par le serveur avec la fonction même
// qui sert à l'enregistrement, pour que l'écran montre ce qui sera écrit.
//
// ⚠️ Décision du 13/10/2026 : la politique d'école (mérite annuel) n'est plus
// reprise ; famille et personnalisée le restent. Le serveur refuse (409
// REPRISE_HISTORIQUE_CHANGEE) un envoi qui s'écarte de son recalcul : ces
// montants doivent donc rester ceux qu'il a fournis. Le repli sur le réel ne
// sert qu'à un serveur antérieur, qui recopiait lui aussi le réel.
const repriseHistoriqueMembre = (member) => {
  const historique = member?.reduction_historique || {};
  return {
    mode: 'historique',
    reduction_inscription: toMoneyNumber(historique.reduction_inscription_reprise ?? historique.reduction_inscription),
    reduction_mensuel: toMoneyNumber(historique.reduction_mensuel_reprise ?? historique.reduction_mensuel),
    motif: 'Reprise des reductions reelles de l annee source'
  };
};

// Part de politique d'école laissée dans l'année source (0 si aucune).
const politiqueNonReprise = (historique = {}) => ({
  inscription: toMoneyNumber(historique.politique_inscription_non_reprise),
  mensuel: toMoneyNumber(historique.politique_mensuel_non_reprise)
});

// Les seules parts non nulles de cette politique, en texte : une inscription
// offerte seule ne doit pas afficher « - 0 FCFA par mois », qui ferait croire
// à une réduction mensuelle retirée. Même règle que le détail par membre.
const textePolitiqueNonReprise = (laissee = {}) => [
  laissee.inscription > 0 && `- ${formatAmount(laissee.inscription)} sur l'entrée`,
  laissee.mensuel > 0 && `- ${formatAmount(laissee.mensuel)} par mois`
].filter(Boolean).join(', ');

const CANDIDATE_PAGE_SIZE = 8;
const HISTORY_PAGE_SIZE = 8;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

const formatDateTime = (value) => {
  if (!value) return 'Non renseigné';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Non renseigné';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const sortByOrderThenName = (a, b) => {
  const orderA = Number(a?.ordre ?? 9999);
  const orderB = Number(b?.ordre ?? 9999);
  if (orderA !== orderB) return orderA - orderB;
  return String(a?.nom || a?.label || '').localeCompare(String(b?.nom || b?.label || ''), 'fr');
};

const SearchableDropdown = ({
  value,
  options,
  onChange,
  placeholder = 'Sélectionner...',
  searchable = true,
  disabled = false,
  emptyLabel = 'Aucune option disponible'
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  const selectedOption = options.find((option) => String(option.value) === String(value));
  const filteredOptions = options.filter((option) =>
    String(option.label || '').toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((current) => !current)}
        className="w-full min-h-[42px] px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-left flex items-center justify-between gap-3 focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown size={17} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">{emptyLabel}</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 hover:bg-[#eef7fb] ${String(value) === String(option.value) ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'
                    }`}
                >
                  <span className="text-sm font-medium">{option.label}</span>
                  {String(value) === String(option.value) && <Check size={16} />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const GroupedClassDropdown = ({ value, classes, sections, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const selectedClass = classes.find((classe) => String(classe.id) === String(value));

  const groupedClasses = useMemo(() => {
    const bySection = classes.reduce((acc, classe) => {
      const sectionId = classe.section_id || 'sans-section';
      if (!acc[sectionId]) acc[sectionId] = [];
      acc[sectionId].push(classe);
      return acc;
    }, {});

    const knownSections = sections
      .filter((section) => bySection[section.id]?.length > 0)
      .sort(sortByOrderThenName)
      .map((section) => ({
        id: section.id,
        nom: section.nom,
        classes: bySection[section.id].sort(sortByOrderThenName)
      }));

    const orphanClasses = bySection['sans-section'] || [];
    if (orphanClasses.length > 0) {
      knownSections.push({
        id: 'sans-section',
        nom: 'Sans section',
        classes: orphanClasses.sort(sortByOrderThenName)
      });
    }

    return knownSections
      .map((section) => ({
        ...section,
        classes: section.classes.filter((classe) =>
          String(classe.nom || '').toLowerCase().includes(query.toLowerCase())
        )
      }))
      .filter((section) => section.classes.length > 0);
  }, [classes, sections, query]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((current) => !current)}
        className="w-full min-h-[42px] px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-left flex items-center justify-between gap-3 focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
      >
        <span className={selectedClass ? 'text-gray-900' : 'text-gray-400'}>
          {selectedClass?.nom || 'Sélectionner une classe'}
        </span>
        <ChevronDown size={17} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher une classe..."
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {groupedClasses.length === 0 ? (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">Aucune classe trouvée</div>
            ) : (
              groupedClasses.map((section) => (
                <div key={section.id}>
                  <div className="px-4 py-2 text-[11px] font-bold uppercase text-[#246990] bg-[#eef7fb]">
                    {section.nom}
                  </div>
                  {section.classes.map((classe) => (
                    <button
                      type="button"
                      key={classe.id}
                      onClick={() => {
                        onChange(classe);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center justify-between gap-3 hover:bg-[#eef7fb] ${String(value) === String(classe.id) ? 'bg-[#e7f3f8] text-[#246990]' : 'text-gray-700'
                        }`}
                    >
                      <span className="text-sm font-medium">{classe.nom}</span>
                      {String(value) === String(classe.id) && <Check size={16} />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, helper, icon: Icon, tone = 'blue' }) => {
  const tones = {
    blue: 'from-[#246990] to-[#3084b5]',
    teal: 'from-[#319190] to-[#63b5b4]',
    clay: 'from-[#c66d59] to-[#ae7467]',
    slate: 'from-[#56667a] to-[#6f7f94]'
  };

  return (
    <div className={`rounded-lg p-5 text-white bg-gradient-to-r ${tones[tone] || tones.blue} shadow-sm`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white/90 font-medium">{label}</p>
          <p className="mt-2 text-3xl font-bold">{value}</p>
          {helper && <p className="mt-2 text-xs text-white/90">{helper}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg bg-white/16 flex items-center justify-center">
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
};

const StepIndicator = ({ currentStep, flowMode = 'individual' }) => {
  const steps = flowMode === 'family'
    ? [
      'Année & famille',
      'Membres',
      'Classes & règles',
      'Frais & paiements',
      'Validation'
    ]
    : [
    'Année & élève',
    'Dossier',
    'Classe & règles',
    'Frais & paiements',
    'Validation'
  ];

  return (
    <div className="flex items-center justify-center">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const active = currentStep >= stepNumber;

        return (
          <div key={step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${active ? 'bg-[#246990] text-white' : 'bg-gray-200 text-gray-500'
                  }`}
              >
                {stepNumber}
              </div>
              <span className={`mt-2 text-xs font-medium ${active ? 'text-[#246990]' : 'text-gray-400'}`}>
                {step}
              </span>
            </div>
            {stepNumber < steps.length && (
              <div className={`w-20 h-1 mx-3 mb-6 ${currentStep > stepNumber ? 'bg-[#246990]' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const ReInscriptionManager = ({ activeYear: activeYearFromParent }) => {
  // Le mode « famille complète » n'est pas un raccourci de saisie : il lit et
  // écrit `familles_nombreuses`. C'est le module familles nombreuses lui-même.
  // Les réductions reprises, elles, portent sur les frais — donc la comptabilité.
  const { estActive } = useFeatures();
  const famillesOuvertes = estActive('module.familles');
  const comptabiliteOuverte = estActive('module.comptabilite');
  // La caisse garde l'argent, pas les places : une reinscription sans
  // encaissement reste legitime caisse fermee — offerte, exoneree, ou payee
  // plus tard. Le serveur ne verifie la caisse que si la reinscription porte
  // reellement de l'argent (reinscription.routes.js).
  //
  // Ce qui manquait n'etait donc pas un verrou mais un avertissement : l'ecran
  // proposait d'encaisser, et le refus se decouvrait apres coup. On presente
  // desormais l'echeancier en lecture seule, en disant pourquoi. Le dossier
  // passe, et personne ne croit avoir encaisse.
  const { estOuverte: caisseOuverte, charge: caisseConnue } = useCaisse();
  // Tenir une caisse est réservé aux profils comptables : sans habilitation,
  // la réinscription passe, impayée, et l'écran dit pourquoi.
  const { habilite: habiliteCaisse } = useHabilitationCaisse();
  const encaissementPossible = comptabiliteOuverte && habiliteCaisse && (!caisseConnue || caisseOuverte);

  const [activeYear, setActiveYear] = useState(activeYearFromParent || null);
  const [targetYear, setTargetYear] = useState(null);
  const [contextBlock, setContextBlock] = useState(null);
  const [reinscriptionMode, setReinscriptionMode] = useState('post_cloture');
  const [previousYears, setPreviousYears] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [classes, setClasses] = useState([]);
  const [classCapacityById, setClassCapacityById] = useState({});
  const [classCapacityLoadingById, setClassCapacityLoadingById] = useState({});
  const [sections, setSections] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [sourceProgress, setSourceProgress] = useState({ total_source: 0, reinscrits: 0, perdus: 0, taux_reinscription: 0 });

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedCandidateLoadingId, setSelectedCandidateLoadingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toastInfo, setToastInfo] = useState(null);

  const [selectedYearId, setSelectedYearId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidatePage, setCandidatePage] = useState(1);
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  const [familyCandidates, setFamilyCandidates] = useState([]);
  const [familySearchLoading, setFamilySearchLoading] = useState(false);
  const [familyPage, setFamilyPage] = useState(1);
  const [bulkFamilyDossier, setBulkFamilyDossier] = useState(null);
  const [bulkMemberIds, setBulkMemberIds] = useState([]);
  const [bulkClassByMember, setBulkClassByMember] = useState({});
  const [bulkReductionStrategy, setBulkReductionStrategy] = useState('historique');
  const [bulkReductionsByMember, setBulkReductionsByMember] = useState({});
  const [bulkPayReinscription, setBulkPayReinscription] = useState(false);
  const [bulkPaidMonthIds, setBulkPaidMonthIds] = useState([]);
  const [bulkMonthlyAmounts, setBulkMonthlyAmounts] = useState({});
  const [bulkPhotoByMember, setBulkPhotoByMember] = useState({});
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkLoadingFamilyId, setBulkLoadingFamilyId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState('');
  const [bulkCameraMemberId, setBulkCameraMemberId] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [flowMode, setFlowMode] = useState('individual');
  const [currentStep, setCurrentStep] = useState(1);

  const [studentForm, setStudentForm] = useState({
    nom: '',
    prenom: '',
    sexe: '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: ''
  });
  const [parentsForm, setParentsForm] = useState([{ ...defaultParent }]);
  const [newClassId, setNewClassId] = useState('');
  const [keepPhoto, setKeepPhoto] = useState(true);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState('');
  const [photoFileName, setPhotoFileName] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [copyDocuments, setCopyDocuments] = useState(true);
  const [resumeFamilyReductions, setResumeFamilyReductions] = useState(false);
  const [familyReductionMode, setFamilyReductionMode] = useState('source');
  const [familyReductions, setFamilyReductions] = useState({
    reduction_inscription: 0,
    reduction_mensuel: 0,
    description: ''
  });
  const [individualReductionMode, setIndividualReductionMode] = useState('none');
  const [individualReductions, setIndividualReductions] = useState({
    reduction_inscription: 0,
    reduction_mensuel: 0,
    motif: '',
    politique_type: 'admis_examen',
    politique_motif: ''
  });
  const [moisScolaires, setMoisScolaires] = useState([]);
  // Date de retour de l'eleve, quand il ne reprend pas des le premier jour.
  const [dateRetour, setDateRetour] = useState('');
  const [traitementMoisRetour, setTraitementMoisRetour] = useState('prorata');
  const [financeForm, setFinanceForm] = useState({
    reinscription: { paye: false, montant_paye: 0 },
    paiements_mensuels: {}
  });
  const photoInputRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const familyFeeDetailsRef = useRef(null);

  const selectedClass = classes.find((classe) => String(classe.id) === String(newClassId));
  const selectedYear = previousYears.find((year) => String(year.id) === String(selectedYearId));
  const documentsTotal = dossier?.documents?.total || 0;
  const sourceFamily = dossier?.famille?.source || null;
  const sourceFamilyMembers = dossier?.famille?.membres || [];
  const sourceFamilyStats = dossier?.famille?.stats || {};
  // L'ADRESSE DE LA PHOTO SOURCE EST LE CHEMIN RELATIF RENDU PAR L'API.
  //
  // POURQUOI l'adresse absolue `https://www.klasys.com${...}` etait fausse : elle demandait
  // la photo au domaine de www QUELLE QUE SOIT l'ecole de l'utilisateur. Depuis csap1, la
  // reinscription allait donc chercher le fichier chez www -- et cela ne << marchait >> que
  // parce que uploads/photos etait un dossier COMMUN a toutes les ecoles, servi en statique
  // et sans jeton.
  //
  // Les photos sont desormais rangees par ecole (uploads/photos/<sous-domaine>/) et l'adresse
  // rendue par l'API est signee pour l'ecole de la requete : une adresse signee pour csap1 ne
  // vaut rien sur le domaine de www. On garde donc le chemin relatif TEL QUEL, signature
  // comprise, comme les cinq autres ecrans qui affichent une photo (OrganigrammeRH,
  // PortailEnseignant, VieScolairePage, MessageForm, ParametresOrganigramme).
  //
  // La valeur sert aussi de drapeau -- `setKeepPhoto(Boolean(sourcePhotoUrl))` et le bouton
  // << Ne pas reprendre >>. Un chemin relatif reste une chaine non vide : rien ne change la.
  const sourcePhotoUrl = dossier?.eleve?.photo_url || null;
  const bulkFamilyMembers = bulkFamilyDossier?.famille?.membres || [];
  const selectedBulkMembers = bulkFamilyMembers.filter((member) => bulkMemberIds.includes(member.id) && !member.deja_reinscrit);
  const bulkTargetMonths = bulkFamilyDossier?.targetMonths || [];
  const progressTotal = Number(sourceProgress.total_source ?? selectedYear?.nb_eleves ?? 0);
  const progressReinscrits = Number(sourceProgress.reinscrits ?? stats.approved ?? 0);
  const progressPerdus = Math.max(0, Number(sourceProgress.perdus ?? (progressTotal - progressReinscrits)));
  const progressRate = progressTotal > 0
    ? Math.round(Number(sourceProgress.taux_reinscription ?? ((progressReinscrits / progressTotal) * 100)))
    : 0;
  // Réinscrits partis depuis (radiés, transférés, supprimés) : le journal les
  // compte toujours, la Liste des élèves non. Les nommer ici fait se recouper
  // les deux écrans (439 ici = 435 actifs là-bas + 4 partis). Même calcul
  // côté serveur pour les deux : services/reinscription/reinscritsPartis.js.
  const progressPartis = Number(sourceProgress.partis || 0);
  const historyTotalPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const historySafePage = Math.min(historyPage, historyTotalPages);
  const historyPageStart = (historySafePage - 1) * HISTORY_PAGE_SIZE;
  const visibleHistory = history.slice(historyPageStart, historyPageStart + HISTORY_PAGE_SIZE);

  const getClassCapacityInfoProps = (classeId) => {
    const key = String(classeId || '');
    return {
      capacity: key ? classCapacityById[key] || null : null,
      loading: key ? Boolean(classCapacityLoadingById[key]) : false
    };
  };

  const loadClassCapacity = async (classeId) => {
    const key = String(classeId || '');
    if (!key || classCapacityById[key] || classCapacityLoadingById[key]) return;

    setClassCapacityLoadingById((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await api.classes.getCapacity(key, targetYear?.id);
      if (response?.success) {
        setClassCapacityById((prev) => ({ ...prev, [key]: response.data }));
      } else {
        setClassCapacityById((prev) => ({ ...prev, [key]: null }));
      }
    } catch (error) {
      console.error('Erreur chargement capacite classe:', error);
      setClassCapacityById((prev) => ({ ...prev, [key]: null }));
    } finally {
      setClassCapacityLoadingById((prev) => ({ ...prev, [key]: false }));
    }
  };

  const selectNewClass = (classeId) => {
    const key = String(classeId || '');
    setNewClassId(key);
    if (key) loadClassCapacity(key);
  };

  const selectBulkClass = (memberId, classeId) => {
    const key = String(classeId || '');
    setBulkClassByMember((prev) => ({ ...prev, [memberId]: key }));
    if (key) loadClassCapacity(key);
  };

  const effectiveFees = useMemo(() => {
    const normalReinscription = toMoneyNumber(selectedClass?.frais_reinscription || selectedClass?.frais_inscription);
    const normalMonthly = toMoneyNumber(selectedClass?.frais_mensuel);
    let inscriptionReduction = 0;
    let monthlyReduction = 0;

    if (resumeFamilyReductions && individualReductionMode === 'family') {
      inscriptionReduction = toMoneyNumber(familyReductions.reduction_inscription);
      monthlyReduction = toMoneyNumber(familyReductions.reduction_mensuel);
    } else if (individualReductionMode === 'exceptionnelle') {
      inscriptionReduction = toMoneyNumber(individualReductions.reduction_inscription);
      monthlyReduction = toMoneyNumber(individualReductions.reduction_mensuel);
    } else if (individualReductionMode === 'politique') {
      inscriptionReduction = normalReinscription;
      monthlyReduction = toMoneyNumber(individualReductions.reduction_mensuel);
    }

    const safeInscriptionReduction = Math.min(normalReinscription, inscriptionReduction);
    const safeMonthlyReduction = Math.min(normalMonthly, monthlyReduction);

    return {
      normalReinscription,
      normalMonthly,
      reinscription: Math.max(0, normalReinscription - safeInscriptionReduction),
      mensuel: Math.max(0, normalMonthly - safeMonthlyReduction),
      reductionInscription: safeInscriptionReduction,
      reductionMensuel: safeMonthlyReduction
    };
  }, [
    selectedClass?.frais_reinscription,
    selectedClass?.frais_inscription,
    selectedClass?.frais_mensuel,
    resumeFamilyReductions,
    individualReductionMode,
    individualReductions.reduction_inscription,
    individualReductions.reduction_mensuel,
    familyReductions.reduction_inscription,
    familyReductions.reduction_mensuel
  ]);

  // Le mois de retour et sa part reellement frequentee. Le montant affiche n'est
  // qu'une estimation : le serveur recalcule a partir du calendrier et fait foi.
  const prorataRetour = useMemo(() => {
    if (!dateRetour || moisScolaires.length === 0) return null;
    const jourRetour = jourCalendrier(dateRetour);
    if (!jourRetour) return null;

    const mois = moisScolaires.find((m) => {
      const debut = jourCalendrier(m.date_debut);
      const fin = jourCalendrier(m.date_fin);
      return debut && fin && debut <= jourRetour && jourRetour <= fin;
    });
    if (!mois) return null;

    const debut = jourCalendrier(mois.date_debut);
    const fin = jourCalendrier(mois.date_fin);
    if (jourRetour <= debut) return null;

    const enJours = (t) => Math.round(Date.parse(`${t}T00:00:00Z`) / 86400000);
    const joursDuMois = enJours(fin) - enJours(debut) + 1;
    const joursPresents = enJours(fin) - enJours(jourRetour) + 1;
    if (joursDuMois <= 0 || joursPresents <= 0 || joursPresents >= joursDuMois) return null;

    const plein = Number(effectiveFees.mensuel) || 0;
    return {
      mois,
      joursPresents,
      joursDuMois,
      montantPlein: plein,
      montantProrata: Math.round(plein * (joursPresents / joursDuMois))
    };
  }, [dateRetour, moisScolaires, effectiveFees.mensuel]);

  const monthsByTrimester = useMemo(() => {
    return moisScolaires.reduce((acc, mois) => {
      const key = mois.trimestre || 'Sans trimestre';
      if (!acc[key]) acc[key] = [];
      acc[key].push(mois);
      return acc;
    }, {});
  }, [moisScolaires]);

  const financeSummary = useMemo(() => {
    const monthlyPayments = Object.values(financeForm.paiements_mensuels || {});
    const monthlyPaid = monthlyPayments.reduce((sum, paiement) => sum + toMoneyNumber(paiement.montant_paye), 0);
    const fixedPaid = financeForm.reinscription?.paye ? effectiveFees.reinscription : 0;

    return {
      totalAttendu: effectiveFees.reinscription + (effectiveFees.mensuel * moisScolaires.length),
      totalPaye: fixedPaid + monthlyPaid,
      moisPayes: monthlyPayments.filter((paiement) => toMoneyNumber(paiement.montant_paye) > 0).length
    };
  }, [effectiveFees.reinscription, effectiveFees.mensuel, financeForm, moisScolaires.length]);

  const yearOptions = previousYears.map((year) => ({
    value: year.id,
    label: `${year.nom} - ${year.nb_eleves || 0} élève${Number(year.nb_eleves || 0) > 1 ? 's' : ''}`
  }));

  const canGoStep2 = flowMode === 'family'
    ? Boolean(bulkFamilyDossier?.famille?.source)
    : Boolean(dossier?.eleve);
  const canGoStep3 = flowMode === 'family'
    ? canGoStep2 && selectedBulkMembers.length > 0
    : canGoStep2 && studentForm.nom && studentForm.prenom && studentForm.sexe && studentForm.date_naissance;
  const canGoStep4 = flowMode === 'family'
    ? canGoStep3 && selectedBulkMembers.every((member) => bulkClassByMember[member.id])
    : canGoStep3 && newClassId && parentsForm.some((parent) => parent.nom_prenom || parent.telephones?.[0]);
  const canGoStep5 = canGoStep4;
  const targetReady = Boolean(targetYear?.preparation?.prete);

  const refreshContext = async () => {
    setLoading(true);
    try {
      const contextResponse = await api.reinscription.getContext();

      if (!contextResponse.success) {
        throw new Error(contextResponse.message || "Impossible de charger le contexte de réinscription");
      }

      const contextData = contextResponse.data || {};
      const localReadOnly = localStorage.getItem('annee_scolaire_read_only') === 'true';

      if (contextData.readOnly || localReadOnly) {
        const currentYear = contextData.currentYear || activeYearFromParent || null;
        setContextBlock({
          currentYear,
          activeYear: contextData.activeYear || null,
          message: contextData.message || "La réinscription n'est pas disponible sur une année archivée."
        });
        setActiveYear(currentYear || contextData.activeYear || activeYearFromParent || null);
        setTargetYear(contextData.activeYear || null);
        setReinscriptionMode(contextData.mode || 'annee_archivee');
        setPreviousYears([]);
        setStats(contextData.stats || { total: 0, approved: 0, pending: 0, rejected: 0 });
        setClasses([]);
        setSections([]);
        setCandidates([]);
        setHistory([]);
        setSourceProgress({ total_source: 0, reinscrits: 0, perdus: 0, taux_reinscription: 0 });
        setSelectedYearId('');
        setSearchTerm('');
        setDossier(null);
        setCurrentStep(1);
        return;
      }

      setContextBlock(null);

      const sectionsResponse = await api.sections.getAll();
      const currentTargetYear = contextData.targetYear || contextData.activeYear || null;
      const classesResponse = currentTargetYear?.id
        ? await api.classes.getAllByYear(currentTargetYear.id)
        : [];
      const loadedClasses = normalizeArrayResponse(classesResponse);
      const loadedSections = normalizeArrayResponse(sectionsResponse);
      const sourceYears = contextData.sourceYears || contextData.previousYears || [];

      setActiveYear(contextData.activeYear || activeYearFromParent || null);
      setTargetYear(currentTargetYear);
      setReinscriptionMode(contextData.mode || 'post_cloture');
      setPreviousYears(sourceYears);
      setStats(contextData.stats || { total: 0, approved: 0, pending: 0, rejected: 0 });
      setClasses(loadedClasses);
      setClassCapacityById({});
      setClassCapacityLoadingById({});
      setSections(loadedSections);
      setSourceProgress({
        total_source: Number(sourceYears?.[0]?.nb_eleves || 0),
        reinscrits: Number(contextData.stats?.approved || 0),
        perdus: Math.max(0, Number(sourceYears?.[0]?.nb_eleves || 0) - Number(contextData.stats?.approved || 0)),
        taux_reinscription: Number(sourceYears?.[0]?.nb_eleves || 0) > 0
          ? Math.round((Number(contextData.stats?.approved || 0) / Number(sourceYears?.[0]?.nb_eleves || 0)) * 100)
          : 0,
        partis: Number(contextData.stats?.partis || 0)
      });

      const firstYear = sourceYears?.[0];
      if (firstYear && !selectedYearId) {
        setSelectedYearId(String(firstYear.id));
      }

      await loadHistory(currentTargetYear?.id || activeYearFromParent?.id);
    } catch (error) {
      console.error('Erreur chargement réinscription:', error);
      setToastInfo({ type: 'error', message: error.message || 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (yearId) => {
    if (!yearId) return;
    const response = await api.reinscription.getHistorique(yearId);
    if (response.success) {
      setHistory(response.data || []);
    }
  };

  useEffect(() => {
    refreshContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedYearId || !targetReady) {
      setCandidates([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await api.reinscription.searchCandidates({
          annee_id: selectedYearId,
          target_annee_id: targetYear?.id,
          q: searchTerm
        });
        if (response.success) {
          setCandidates(response.data || []);
          if (response.meta?.progress) {
            setSourceProgress(response.meta.progress);
          }
        } else {
          setToastInfo({ type: 'error', message: response.message || 'Erreur lors de la recherche' });
        }
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [selectedYearId, searchTerm, targetYear?.id, targetReady]);

  useEffect(() => {
    setCandidatePage(1);
  }, [selectedYearId, searchTerm]);

  useEffect(() => {
    setFamilyPage(1);
  }, [selectedYearId, familySearchTerm]);

  useEffect(() => {
    // Sans le module familles nombreuses, cette route répond 403 : l'appel
    // partait pourtant même en mode « élève seul », dès qu'une année source
    // était choisie.
    if (!famillesOuvertes || !selectedYearId || !targetReady) {
      setFamilyCandidates([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setFamilySearchLoading(true);
      try {
        const response = await api.reinscription.searchFamilies({
          annee_id: selectedYearId,
          target_annee_id: targetYear?.id,
          q: familySearchTerm
        });

        if (response.success) {
          setFamilyCandidates(response.data || []);
        }
      } finally {
        setFamilySearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [famillesOuvertes, selectedYearId, familySearchTerm, targetYear?.id, targetReady]);

  useEffect(() => {
    setHistoryPage(1);
  }, [history.length]);

  useEffect(() => {
    setFinanceForm((prev) => {
      const nextMonthly = {};

      moisScolaires.forEach((mois) => {
        const current = prev.paiements_mensuels?.[mois.id] || {};
        const currentAmount = current.paye ? effectiveFees.mensuel : toMoneyNumber(current.montant_paye);
        const safeAmount = Math.min(effectiveFees.mensuel, currentAmount);

        nextMonthly[mois.id] = {
          ...current,
          montant_attendu: effectiveFees.mensuel,
          montant_paye: safeAmount,
          paye: effectiveFees.mensuel > 0 && safeAmount >= effectiveFees.mensuel
        };
      });

      return {
        reinscription: {
          ...(prev.reinscription || {}),
          montant_attendu: effectiveFees.reinscription,
          montant_paye: prev.reinscription?.paye ? effectiveFees.reinscription : 0
        },
        paiements_mensuels: nextMonthly
      };
    });
  }, [effectiveFees.reinscription, effectiveFees.mensuel, moisScolaires]);

  const stopPhotoCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setCameraActive(false);
    setBulkCameraMemberId(null);
  };

  useEffect(() => () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
  }, []);

  const handlePhotoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_PHOTO_SIZE) {
      setCameraError("La photo ne doit pas depasser 5MB.");
      event.target.value = '';
      return;
    }

    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
      setCameraError("Formats acceptes : JPG, PNG ou WEBP.");
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = String(reader.result || '');
      setPhotoDataUrl(dataUrl);
      setPhotoPreview(dataUrl);
      setPhotoFileName(file.name || `photo_reinscription_${Date.now()}.jpg`);
      setKeepPhoto(false);
      setCameraError('');
      stopPhotoCamera();
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const startPhotoCamera = async () => {
    try {
      setCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La camera n'est pas disponible dans ce navigateur.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 720 },
          height: { ideal: 720 }
        },
        audio: false
      });

      cameraStreamRef.current = stream;
      setBulkCameraMemberId(null);
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 0);
    } catch (error) {
      console.error('Erreur ouverture camera reinscription:', error);
      setCameraError("Impossible d'ouvrir la camera. Verifiez les autorisations du navigateur.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError("La camera n'est pas encore prete.");
      return;
    }

    const canvas = document.createElement('canvas');
    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const cropSize = Math.min(sourceWidth, sourceHeight);
    const sourceX = Math.max(0, (sourceWidth - cropSize) / 2);
    const sourceY = Math.max(0, (sourceHeight - cropSize) / 2);

    canvas.width = 720;
    canvas.height = 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, sourceX, sourceY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setPhotoDataUrl(dataUrl);
    setPhotoPreview(dataUrl);
    setPhotoFileName(`photo_reinscription_${Date.now()}.jpg`);
    setKeepPhoto(false);
    setCameraError('');
    stopPhotoCamera();
  };

  const clearCustomPhoto = () => {
    setPhotoDataUrl('');
    setPhotoPreview(null);
    setPhotoFileName('');
    setCameraError('');
    stopPhotoCamera();
    setKeepPhoto(Boolean(sourcePhotoUrl));
  };

  const setBulkPhotoChoice = (memberId, dataUrl, fileName) => {
    setBulkPhotoByMember((prev) => ({
      ...prev,
      [memberId]: {
        preview: dataUrl,
        dataUrl,
        fileName: fileName || `photo_reinscription_${memberId}_${Date.now()}.jpg`,
        keepPhoto: false
      }
    }));
  };

  const handleBulkPhotoFileChange = (memberId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_PHOTO_SIZE) {
      setCameraError("La photo ne doit pas depasser 5MB.");
      event.target.value = '';
      return;
    }

    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/i)) {
      setCameraError("Formats acceptes : JPG, PNG ou WEBP.");
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = String(reader.result || '');
      setBulkPhotoChoice(memberId, dataUrl, file.name);
      setCameraError('');
      stopPhotoCamera();
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const startBulkPhotoCamera = async (memberId) => {
    try {
      setCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("La camera n'est pas disponible dans ce navigateur.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 720 },
          height: { ideal: 720 }
        },
        audio: false
      });

      cameraStreamRef.current = stream;
      setBulkCameraMemberId(memberId);
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 0);
    } catch (error) {
      console.error('Erreur ouverture camera reinscription familiale:', error);
      setCameraError("Impossible d'ouvrir la camera. Verifiez les autorisations du navigateur.");
    }
  };

  const captureBulkPhoto = () => {
    const video = videoRef.current;
    if (!bulkCameraMemberId || !video || !video.videoWidth || !video.videoHeight) {
      setCameraError("La camera n'est pas encore prete.");
      return;
    }

    const canvas = document.createElement('canvas');
    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const cropSize = Math.min(sourceWidth, sourceHeight);
    const sourceX = Math.max(0, (sourceWidth - cropSize) / 2);
    const sourceY = Math.max(0, (sourceHeight - cropSize) / 2);

    canvas.width = 720;
    canvas.height = 720;
    const context = canvas.getContext('2d');
    context.drawImage(video, sourceX, sourceY, cropSize, cropSize, 0, 0, canvas.width, canvas.height);

    setBulkPhotoChoice(bulkCameraMemberId, canvas.toDataURL('image/jpeg', 0.9));
    setCameraError('');
    stopPhotoCamera();
  };

  const clearBulkPhoto = (memberId, keepPhoto = true) => {
    setBulkPhotoByMember((prev) => ({
      ...prev,
      [memberId]: {
        preview: '',
        dataUrl: '',
        fileName: '',
        keepPhoto
      }
    }));
    setCameraError('');
    stopPhotoCamera();
  };

  const resetFlow = () => {
    stopPhotoCamera();
    setDossier(null);
    setFlowMode('individual');
    setCurrentStep(1);
    setStudentForm({
      nom: '',
      prenom: '',
      sexe: '',
      date_naissance: '',
      lieu_naissance: '',
      nationalite: ''
    });
    setParentsForm([{ ...defaultParent }]);
    setNewClassId('');
    setKeepPhoto(true);
    setPhotoPreview(null);
    setPhotoDataUrl('');
    setPhotoFileName('');
    setCameraError('');
    setCopyDocuments(true);
    setResumeFamilyReductions(false);
    setFamilyReductionMode('source');
    setFamilyReductions({
      reduction_inscription: 0,
      reduction_mensuel: 0,
      description: ''
    });
    setIndividualReductionMode('none');
    setIndividualReductions({
      reduction_inscription: 0,
      reduction_mensuel: 0,
      motif: '',
      politique_type: 'admis_examen',
      politique_motif: ''
    });
    setClassCapacityById({});
    setClassCapacityLoadingById({});
    setBulkFamilyDossier(null);
    setBulkMemberIds([]);
    setBulkClassByMember({});
    setBulkReductionStrategy('historique');
    setBulkReductionsByMember({});
    setBulkPayReinscription(false);
    setBulkPaidMonthIds([]);
    setBulkMonthlyAmounts({});
    setBulkPhotoByMember({});
    setBulkCameraMemberId(null);
    setBulkProgress('');
    setMoisScolaires([]);
    setFinanceForm({
      reinscription: { paye: false, montant_paye: 0 },
      paiements_mensuels: {}
    });
  };

  const normalizeClassLabel = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\s._-]+/g, '');

  const getClassProgressionKey = (value) => {
    const label = normalizeClassLabel(value);
    if (!label) return '';

    if (label === 'garderie') return 'garderie';
    if (/^p[123]$/.test(label)) return label;
    if (/^cp[12]$/.test(label)) return label;
    if (/^ce[12]$/.test(label)) return label;
    if (/^cm[12]$/.test(label)) return label;
    if (/^(6e|6eme|6ieme|sixieme)/.test(label)) return '6e';
    if (/^(5e|5eme|5ieme|cinquieme)/.test(label)) return '5e';
    if (/^(4e|4eme|4ieme|quatrieme)/.test(label)) return '4e';
    if (/^(3e|3eme|3ieme|troisieme)/.test(label)) return '3e';
    if (/^(seconde|2nde|2de)/.test(label)) return 'seconde';
    if (/^(premiere|1ere|1re)/.test(label)) return 'premiere';
    if (/^(terminale|tle)/.test(label)) return 'terminale';
    return label;
  };

  const isPlainProgressionClass = (classe, progressionKey) => {
    const label = normalizeClassLabel(classe?.nom);
    const plainLabels = {
      garderie: ['garderie'],
      p1: ['p1'],
      p2: ['p2'],
      p3: ['p3'],
      cp1: ['cp1'],
      cp2: ['cp2'],
      ce1: ['ce1'],
      ce2: ['ce2'],
      cm1: ['cm1'],
      cm2: ['cm2'],
      '6e': ['6e', '6eme', '6ieme', 'sixieme'],
      '5e': ['5e', '5eme', '5ieme', 'cinquieme'],
      '4e': ['4e', '4eme', '4ieme', 'quatrieme'],
      '3e': ['3e', '3eme', '3ieme', 'troisieme'],
      seconde: ['seconde', '2nde', '2de'],
      premiere: ['premiere', '1ere', '1re'],
      terminale: ['terminale', 'tle']
    };
    return (plainLabels[progressionKey] || [progressionKey]).includes(label);
  };

  const classProgression = [
    'garderie',
    'p1',
    'p2',
    'p3',
    'cp1',
    'cp2',
    'ce1',
    'ce2',
    'cm1',
    'cm2',
    '6e',
    '5e',
    '4e',
    '3e',
    'seconde',
    'premiere',
    'terminale'
  ];

  const findEquivalentTargetClass = (eleve) => {
    if (!eleve) return null;

    const sameSectionAndName = classes.find((classe) =>
      String(classe.nom || '').trim().toLowerCase() === String(eleve.classe_nom || '').trim().toLowerCase()
      && String(classe.section_id || '') === String(eleve.classe_section_id || '')
    );

    if (sameSectionAndName) return sameSectionAndName;

    return classes.find((classe) =>
      String(classe.nom || '').trim().toLowerCase() === String(eleve.classe_nom || '').trim().toLowerCase()
    ) || null;
  };

  const findPromotionTargetClass = (eleve) => {
    const sourceKey = getClassProgressionKey(eleve?.classe_nom);
    const sourceIndex = classProgression.indexOf(sourceKey);
    if (sourceIndex < 0 || sourceIndex >= classProgression.length - 1) return null;

    const targetKey = classProgression[sourceIndex + 1];
    const candidates = classes.filter((classe) => getClassProgressionKey(classe.nom) === targetKey);
    if (candidates.length === 0) return null;

    return candidates.find((classe) => isPlainProgressionClass(classe, targetKey)) || null;
  };

  const loadCandidateDetails = async (candidate) => {
    setSelectedCandidateLoadingId(candidate.id);
    setDetailsLoading(true);
    try {
      const response = await api.reinscription.getCandidateDetails(candidate.id, selectedYearId, targetYear?.id);

      if (!response.success) {
        throw new Error(response.message || 'Impossible de charger le dossier');
      }

      const details = response.data;
      const eleve = details.eleve;
      const parents = details.parents?.length ? details.parents : [{ ...defaultParent }];
      const familyRules = details.famille?.source || null;

      setFlowMode('individual');
      setBulkFamilyDossier(null);
      setBulkMemberIds([]);
      setBulkPhotoByMember({});
      setBulkReductionStrategy('historique');
      setBulkReductionsByMember({});
      setDossier(details);
      setStudentForm({
        nom: eleve.nom || '',
        prenom: eleve.prenom || '',
        sexe: eleve.sexe || '',
        date_naissance: formatInputDate(eleve.date_naissance),
        lieu_naissance: eleve.lieu_naissance || '',
        nationalite: eleve.nationalite || ''
      });
      setParentsForm(
        parents.map((parent, index) => ({
          relation: parent.relation || parent.lien_parente || (index === 0 ? 'tuteur' : 'autre'),
          nom_prenom: parent.nom_prenom || '',
          email: parent.email || '',
          telephones: parent.telephones?.length
            ? parent.telephones
            : [parent.telephone, parent.telephone_secondaire].filter(Boolean).length
              ? [parent.telephone, parent.telephone_secondaire].filter(Boolean)
              : [''],
          adresse: parent.adresse || '',
          principal: index === 0 || Boolean(parent.principal)
        }))
      );
      const promotionClass = findPromotionTargetClass(eleve);
      selectNewClass(promotionClass?.id || '');
      setKeepPhoto(Boolean(eleve.photo_url));
      setPhotoPreview(null);
      setPhotoDataUrl('');
      setPhotoFileName('');
      setCameraError('');
      stopPhotoCamera();
      setCopyDocuments((details.documents?.total || 0) > 0);
      // ⚠️ Masquer la case ne suffit pas : sans cette garde, l'état restait
      // « famille » pour un élève rattaché, et la validation envoyait
      // `reprendre_famille_reductions: true` — le serveur aurait recréé une
      // famille dans une école qui n'a pas le module.
      const reprendFamille = famillesOuvertes && Boolean(eleve.famille_id);
      setResumeFamilyReductions(reprendFamille);
      setIndividualReductionMode(reprendFamille ? 'family' : 'none');
      setIndividualReductions({
        reduction_inscription: 0,
        reduction_mensuel: 0,
        motif: '',
        politique_type: eleve.politique_type || 'admis_examen',
        politique_motif: eleve.politique_motif || ''
      });
      setFamilyReductionMode('source');
      setFamilyReductions({
        reduction_inscription: toMoneyNumber(familyRules?.reduction_inscription),
        reduction_mensuel: toMoneyNumber(familyRules?.reduction_mensuel),
        description: familyRules?.description || ''
      });
      setMoisScolaires(details.targetMonths || []);
      setFinanceForm({
        reinscription: { paye: false, montant_paye: 0 },
        paiements_mensuels: {}
      });
      setCurrentStep(2);
    } catch (error) {
      setToastInfo({ type: 'error', message: error.message || 'Erreur lors du chargement du dossier' });
    } finally {
      setDetailsLoading(false);
      setSelectedCandidateLoadingId(null);
    }
  };

  const loadFamilyDetails = async (famille) => {
    setBulkLoadingFamilyId(famille.id);
    try {
      const response = await api.reinscription.getFamilyDetails(famille.id, selectedYearId, targetYear?.id);

      if (!response.success) {
        throw new Error(response.message || 'Impossible de charger la famille');
      }

      const data = response.data;
      const members = data?.famille?.membres || [];
      const selectedMembers = members.filter((member) => !member.deja_reinscrit);
      const nextClassByMember = {};
      const initialReductionsByMember = {};

      selectedMembers.forEach((member) => {
        const promotionClass = findPromotionTargetClass(member);
        if (promotionClass?.id) {
          nextClassByMember[member.id] = String(promotionClass.id);
        }

        initialReductionsByMember[member.id] = repriseHistoriqueMembre(member);
      });

      setBulkFamilyDossier(data);
      setBulkMemberIds(selectedMembers.map((member) => member.id));
      setBulkClassByMember(nextClassByMember);
      Object.values(nextClassByMember).forEach((classeId) => loadClassCapacity(classeId));
      setBulkReductionStrategy('historique');
      setBulkReductionsByMember(initialReductionsByMember);
      setBulkPayReinscription(false);
      setBulkPaidMonthIds([]);
      setBulkMonthlyAmounts({});
      setBulkPhotoByMember({});
      setFamilyReductionMode('source');
      setFamilyReductions({
        reduction_inscription: toMoneyNumber(data?.famille?.source?.reduction_inscription),
        reduction_mensuel: toMoneyNumber(data?.famille?.source?.reduction_mensuel),
        description: data?.famille?.source?.description || ''
      });
      setResumeFamilyReductions(true);
      setIndividualReductionMode('family');
      setFlowMode('family');
      setDossier(null);
      setBulkProgress('');
      setCurrentStep(2);
    } catch (error) {
      setToastInfo({ type: 'error', message: error.message || 'Erreur lors du chargement de la famille' });
    } finally {
      setBulkLoadingFamilyId(null);
    }
  };

  const toggleBulkMember = (memberId) => {
    setBulkMemberIds((prev) => (
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    ));
  };

  const toggleBulkMonth = (monthId) => {
    setBulkPaidMonthIds((prev) => (
      prev.includes(monthId)
        ? prev.filter((id) => id !== monthId)
        : [...prev, monthId]
    ));
  };

  const getBulkMemberTargetFees = () => selectedBulkMembers.map((member) => {
    const targetClass = classes.find((classe) => String(classe.id) === String(bulkClassByMember[member.id]));
    const normalReinscription = toMoneyNumber(targetClass?.frais_reinscription || targetClass?.frais_inscription);
    const normalMonthly = toMoneyNumber(targetClass?.frais_mensuel);
    const choice = getMemberReductionChoice(member);
    const reductionReinscription = Math.min(normalReinscription, toMoneyNumber(choice.reduction_inscription));
    const reductionMensuel = Math.min(normalMonthly, toMoneyNumber(choice.reduction_mensuel));

    return {
      member,
      targetClass,
      normalReinscription,
      normalMonthly,
      reductionReinscription,
      reductionMensuel,
      reinscription: Math.max(0, normalReinscription - reductionReinscription),
      mensuel: Math.max(0, normalMonthly - reductionMensuel)
    };
  });

  const updateBulkMonthAmount = (monthId, value) => {
    const amount = toMoneyNumber(value);
    setBulkMonthlyAmounts((prev) => ({
      ...prev,
      [monthId]: amount
    }));
    setBulkPaidMonthIds((prev) => {
      const exists = prev.includes(monthId);
      if (amount > 0 && !exists) return [...prev, monthId];
      if (amount <= 0 && exists) return prev.filter((id) => id !== monthId);
      return prev;
    });
  };

  const distributeFamilyMonthAmount = (monthId, rows = getBulkMemberTargetFees()) => {
    const totalDue = rows.reduce((sum, row) => sum + toMoneyNumber(row.mensuel), 0);
    const cappedAmount = Math.min(toMoneyNumber(bulkMonthlyAmounts[monthId]), totalDue);
    if (cappedAmount <= 0 || totalDue <= 0) {
      return {};
    }

    const allocations = {};
    let allocated = 0;
    const sortableRows = rows
      .filter((row) => toMoneyNumber(row.mensuel) > 0)
      .map((row) => {
        const exact = (cappedAmount * toMoneyNumber(row.mensuel)) / totalDue;
        const floorAmount = Math.floor(exact);
        allocations[row.member.id] = Math.min(toMoneyNumber(row.mensuel), floorAmount);
        allocated += allocations[row.member.id];
        return {
          ...row,
          remainder: exact - floorAmount
        };
      })
      .sort((a, b) => b.remainder - a.remainder);

    let remaining = cappedAmount - allocated;
    while (remaining > 0) {
      let moved = false;
      for (const row of sortableRows) {
        const current = allocations[row.member.id] || 0;
        const capacity = toMoneyNumber(row.mensuel) - current;
        if (capacity <= 0) continue;
        allocations[row.member.id] = current + 1;
        remaining -= 1;
        moved = true;
        if (remaining <= 0) break;
      }
      if (!moved) break;
    }

    return allocations;
  };

  const getMemberReductionChoice = (member) => {
    if (bulkReductionStrategy === 'reference') {
      return {
        mode: 'reference',
        reduction_inscription: toMoneyNumber(familyReductions.reduction_inscription),
        reduction_mensuel: toMoneyNumber(familyReductions.reduction_mensuel),
        motif: 'Reduction de reference de la famille'
      };
    }

    if (bulkReductionStrategy === 'aucune') {
      return {
        mode: 'aucune',
        reduction_inscription: 0,
        reduction_mensuel: 0,
        motif: 'Aucune reduction reprise'
      };
    }

    return bulkReductionsByMember[member.id] || repriseHistoriqueMembre(member);
  };

  const scrollToFamilyFeeDetails = () => {
    window.setTimeout(() => {
      familyFeeDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const applyBulkReductionStrategy = (strategy, shouldScroll = true) => {
    setBulkReductionStrategy(strategy);
    setBulkReductionsByMember(() => {
      const next = {};
      selectedBulkMembers.forEach((member) => {
        if (strategy === 'reference') {
          next[member.id] = {
            mode: 'reference',
            reduction_inscription: toMoneyNumber(familyReductions.reduction_inscription),
            reduction_mensuel: toMoneyNumber(familyReductions.reduction_mensuel),
            motif: 'Reduction de reference de la famille'
          };
        } else if (strategy === 'aucune') {
          next[member.id] = {
            mode: 'aucune',
            reduction_inscription: 0,
            reduction_mensuel: 0,
            motif: 'Aucune reduction reprise'
          };
        } else {
          next[member.id] = {
            ...repriseHistoriqueMembre(member),
            mode: strategy === 'personnalisee' ? 'personnalisee' : 'historique'
          };
        }
      });
      return next;
    });
    if (shouldScroll) scrollToFamilyFeeDetails();
  };

  const updateBulkMemberReduction = (memberId, field, value) => {
    setBulkReductionStrategy('personnalisee');
    setBulkReductionsByMember((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || { mode: 'personnalisee', reduction_inscription: 0, reduction_mensuel: 0 }),
        mode: 'personnalisee',
        [field]: toMoneyNumber(value),
        motif: 'Reduction ajustee lors de la reinscription'
      }
    }));
  };

  const formatReductionMode = (mode) => {
    if (mode === 'famille') return 'Reference famille';
    if (mode === 'personnalisee') return 'Personnalisee';
    if (mode === 'historique') return 'Historique';
    if (mode === 'reference') return 'Reference famille';
    if (mode === 'exceptionnelle') return 'Exceptionnelle';
    if (mode === 'mixte') return 'Mixte';
    return 'Aucune';
  };

  // Étiquette de la réduction lue dans l'année source : une exceptionnelle
  // portée par la politique d'école est nommée comme telle, puisque c'est la
  // part qui n'est pas reprise. La politique se lit sur ce qu'elle laisse, à
  // l'entrée OU au mois : une inscription offerte seule laisse le mode mensuel
  // à « aucune » et serait sinon étiquetée « Aucune ».
  const formatSourceReductionMode = (historique = {}) => {
    const laissee = politiqueNonReprise(historique);
    const porteUnePolitique = Boolean(historique.politique_ecole)
      && (laissee.inscription > 0 || laissee.mensuel > 0);
    if (!porteUnePolitique) return formatReductionMode(historique.mode_mensuel);
    if (!historique.mode_mensuel || ['aucune', 'exceptionnelle'].includes(historique.mode_mensuel)) {
      return "Politique d'école";
    }
    return `${formatReductionMode(historique.mode_mensuel)} - politique d'école`;
  };

  const handleBulkFamilySubmit = async () => {
    if (!bulkFamilyDossier?.famille?.source || bulkMemberIds.length === 0) return;

    setBulkSubmitting(true);
    setBulkProgress('');
    try {
      const members = bulkFamilyDossier.famille.membres || [];
      const selectedMembers = members.filter((member) => bulkMemberIds.includes(member.id) && !member.deja_reinscrit);
      const targetFeeRows = getBulkMemberTargetFees();
      const monthlyAllocations = {};
      bulkTargetMonths.forEach((month) => {
        monthlyAllocations[month.id] = distributeFamilyMonthAmount(month.id, targetFeeRows);
      });

      for (let index = 0; index < selectedMembers.length; index += 1) {
        const member = selectedMembers[index];
        const selectedClassId = bulkClassByMember[member.id];

        if (!selectedClassId) {
          throw new Error(`Classe cible manquante pour ${member.prenom} ${member.nom}`);
        }

        setBulkProgress(`${index + 1}/${selectedMembers.length} - ${member.prenom} ${member.nom}`);

        const detailsResponse = await api.reinscription.getCandidateDetails(member.id, selectedYearId, targetYear?.id);
        if (!detailsResponse.success) {
          throw new Error(detailsResponse.message || `Impossible de charger ${member.prenom} ${member.nom}`);
        }

        const details = detailsResponse.data;
        const eleve = details.eleve || member;
        const parents = details.parents?.length ? details.parents : [{ ...defaultParent }];
        const photoChoice = bulkPhotoByMember[member.id] || {};
        const memberReductionChoice = getMemberReductionChoice(member);
        const monthlyPayload = {};
        bulkTargetMonths.forEach((month) => {
          const memberAmount = toMoneyNumber(monthlyAllocations[month.id]?.[member.id]);
          if (memberAmount > 0) {
            monthlyPayload[month.id] = { montant_paye: memberAmount };
          }
        });

        const payload = {
          source_eleve_id: eleve.id,
          source_annee_id: selectedYearId,
          cible_annee_id: targetYear?.id,
          nouvelle_classe_id: selectedClassId,
          eleve: {
            nom: eleve.nom || '',
            prenom: eleve.prenom || '',
            sexe: eleve.sexe || '',
            date_naissance: formatInputDate(eleve.date_naissance),
            lieu_naissance: eleve.lieu_naissance || '',
            nationalite: eleve.nationalite || ''
          },
          parents: parents.map((parent, parentIndex) => ({
            relation: parent.relation || parent.lien_parente || (parentIndex === 0 ? 'tuteur' : 'autre'),
            nom_prenom: parent.nom_prenom || '',
            email: parent.email || '',
            telephones: parent.telephones?.length
              ? parent.telephones
              : [parent.telephone, parent.telephone_secondaire].filter(Boolean),
            telephone: parent.telephones?.[0] || parent.telephone || '',
            telephone_secondaire: parent.telephones?.[1] || parent.telephone_secondaire || null,
            adresse: parent.adresse || '',
            principal: parentIndex === 0
          })),
          keep_photo: photoChoice.dataUrl ? false : photoChoice.keepPhoto !== false,
          photo_data_url: photoChoice.dataUrl || null,
          photo_filename: photoChoice.fileName || null,
          copy_documents: true,
          reprendre_famille_reductions: true,
          famille_reduction_mode: bulkReductionStrategy === 'reference' ? 'source' : 'per_member',
          famille_reductions: familyReductions,
          reduction_eleve: {
            mode: memberReductionChoice.mode,
            reduction_inscription: memberReductionChoice.reduction_inscription,
            reduction_mensuel: memberReductionChoice.reduction_mensuel,
            motif: memberReductionChoice.motif || ''
          },
          finance: {
            reinscription: { paye: bulkPayReinscription },
            paiements_mensuels: monthlyPayload
          }
        };

        const createResponse = await api.reinscription.create(payload);
        if (!createResponse.success) {
          const apiError = new Error(createResponse.message || `Echec de la reinscription de ${member.prenom} ${member.nom}`);
          apiError.code = createResponse.code;
          apiError.status = createResponse.status;
          apiError.data = createResponse.data;
          throw apiError;
        }
      }

      setToastInfo({ type: 'success', message: `${selectedMembers.length} membre(s) reinscrit(s) avec succes` });
      resetFlow();
      await refreshContext();
    } catch (error) {
      const message = error.message || 'Erreur lors de la reinscription familiale';
      const isSeuilError = error.code === 'CLASSE_SEUIL_ATTEINT' || /seuil/i.test(message);
      // Réductions reprises recalculées autrement par le serveur (écran périmé
      // ou dossier source modifié) : rien n'est écrit pour ce membre, il faut
      // recharger la page puis la famille. Le message reste affiché jusqu'à sa
      // lecture.
      const isRepriseChangee = error.code === 'REPRISE_HISTORIQUE_CHANGEE';
      setToastInfo({
        type: 'error',
        message,
        long: isSeuilError || isRepriseChangee,
        title: isSeuilError
          ? 'Seuil de classe atteint'
          : isRepriseChangee ? 'Dossier source modifié' : undefined
      });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const updateParent = (index, field, value) => {
    setParentsForm((prev) => prev.map((parent, currentIndex) => {
      if (currentIndex !== index) return parent;
      return { ...parent, [field]: value };
    }));
  };

  const updateParentPhone = (parentIndex, phoneIndex, value) => {
    setParentsForm((prev) => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      const telephones = [...(parent.telephones || [''])];
      telephones[phoneIndex] = value;
      return { ...parent, telephones };
    }));
  };

  const addParentPhone = (parentIndex) => {
    setParentsForm((prev) => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      if ((parent.telephones || []).length >= 2) return parent;
      return { ...parent, telephones: [...(parent.telephones || ['']), ''] };
    }));
  };

  const removeParentPhone = (parentIndex, phoneIndex) => {
    setParentsForm((prev) => prev.map((parent, currentIndex) => {
      if (currentIndex !== parentIndex) return parent;
      const telephones = (parent.telephones || ['']).filter((_, index) => index !== phoneIndex);
      return { ...parent, telephones: telephones.length ? telephones : [''] };
    }));
  };

  const addParent = () => {
    setParentsForm((prev) => [
      ...prev,
      {
        ...defaultParent,
        principal: false,
        relation: prev.length === 1 ? 'mere' : 'autre'
      }
    ]);
  };

  const removeParent = (index) => {
    setParentsForm((prev) => {
      const next = prev.filter((_, currentIndex) => currentIndex !== index);
      return next.length ? next.map((parent, currentIndex) => ({ ...parent, principal: currentIndex === 0 })) : [{ ...defaultParent }];
    });
  };

  const updateFamilyReduction = (field, value) => {
    setFamilyReductionMode('custom');
    setFamilyReductions((prev) => ({
      ...prev,
      [field]: field === 'description' ? value : toMoneyNumber(value)
    }));
  };

  const applySourceFamilyReductions = () => {
    const familyRules = sourceFamily;
    setIndividualReductionMode('family');
    setResumeFamilyReductions(true);
    setFamilyReductionMode('source');
    setFamilyReductions({
      reduction_inscription: toMoneyNumber(familyRules?.reduction_inscription),
      reduction_mensuel: toMoneyNumber(familyRules?.reduction_mensuel),
      description: familyRules?.description || ''
    });
  };

  const selectIndividualReductionMode = (mode) => {
    setIndividualReductionMode(mode);

    if (mode === 'family') {
      applySourceFamilyReductions();
      return;
    }

    setResumeFamilyReductions(false);
  };

  const updateIndividualReduction = (field, value) => {
    setIndividualReductions((prev) => ({
      ...prev,
      [field]: ['motif', 'politique_type', 'politique_motif'].includes(field)
        ? value
        : toMoneyNumber(value)
    }));
  };

  const setReinscriptionFeePaid = (checked) => {
    setFinanceForm((prev) => ({
      ...prev,
      reinscription: {
        ...(prev.reinscription || {}),
        paye: checked,
        montant_paye: checked ? effectiveFees.reinscription : 0,
        montant_attendu: effectiveFees.reinscription
      }
    }));
  };

  // Le montant reellement du pour un mois : le tarif plein d'ordinaire, le
  // prorata sur le mois de reprise. Sans ce plafond, « Tout payer » encaissait
  // le tarif plein sur un mois proratise et creait un trop-percu invisible.
  const montantDuMois = (moisId) => (
    prorataRetour && traitementMoisRetour === 'prorata' && String(prorataRetour.mois.id) === String(moisId)
      ? prorataRetour.montantProrata
      : effectiveFees.mensuel
  );

  const updateMonthlyPayment = (moisId, value) => {
    const montantDu = montantDuMois(moisId);
    const amount = Math.min(montantDu, toMoneyNumber(value));
    setFinanceForm((prev) => ({
      ...prev,
      paiements_mensuels: {
        ...(prev.paiements_mensuels || {}),
        [moisId]: {
          ...(prev.paiements_mensuels?.[moisId] || {}),
          montant_attendu: montantDu,
          montant_paye: amount,
          paye: montantDu > 0 && amount >= montantDu
        }
      }
    }));
  };

  const markAllMonths = (paid) => {
    setFinanceForm((prev) => {
      const nextMonthly = {};
      moisScolaires.forEach((mois) => {
        const montantDu = montantDuMois(mois.id);
        nextMonthly[mois.id] = {
          ...(prev.paiements_mensuels?.[mois.id] || {}),
          montant_attendu: montantDu,
          montant_paye: paid ? montantDu : 0,
          paye: paid
        };
      });

      return {
        ...prev,
        paiements_mensuels: nextMonthly
      };
    });
  };

  const handleSubmit = async () => {
    if (!dossier?.eleve || !canGoStep5) return;

    setSubmitting(true);
    try {
      const reductionElevePayload = individualReductionMode === 'family'
        ? { mode: 'reference' }
        : individualReductionMode === 'exceptionnelle'
          ? {
            mode: 'exceptionnelle',
            reduction_inscription: effectiveFees.reductionInscription,
            reduction_mensuel: effectiveFees.reductionMensuel,
            motif: individualReductions.motif || 'Reduction exceptionnelle appliquee a la reinscription'
          }
          : individualReductionMode === 'politique'
            ? {
              mode: 'politique',
              reduction_inscription: effectiveFees.reductionInscription,
              reduction_mensuel: effectiveFees.reductionMensuel,
              politique_type: individualReductions.politique_type || 'admis_examen',
              motif: individualReductions.politique_motif || 'Politique d ecole appliquee a la reinscription'
            }
            : { mode: 'aucune' };

      const payload = {
        source_eleve_id: dossier.eleve.id,
        source_annee_id: selectedYearId,
        cible_annee_id: targetYear?.id,
        nouvelle_classe_id: newClassId,
        eleve: studentForm,
        parents: parentsForm.map((parent, index) => ({
          ...parent,
          principal: index === 0,
          telephone: parent.telephones?.[0] || '',
          telephone_secondaire: parent.telephones?.[1] || null
        })),
        keep_photo: photoDataUrl ? false : keepPhoto,
        photo_data_url: photoDataUrl || null,
        photo_filename: photoFileName || null,
        copy_documents: copyDocuments,
        reprendre_famille_reductions: individualReductionMode === 'family' && resumeFamilyReductions,
        famille_reduction_mode: individualReductionMode === 'family' && resumeFamilyReductions ? familyReductionMode : 'none',
        famille_reductions: individualReductionMode === 'family' && resumeFamilyReductions ? familyReductions : null,
        reduction_eleve: reductionElevePayload,
        // Le mois de retour au prorata : on envoie l'intention et la date, pas le
        // montant. Le calcul appartient au serveur, qui lit le calendrier.
        finance: (() => {
          if (!prorataRetour || traitementMoisRetour !== 'prorata') return financeForm;
          const cle = String(prorataRetour.mois.id);
          return {
            ...financeForm,
            paiements_mensuels: {
              ...(financeForm.paiements_mensuels || {}),
              [cle]: {
                ...(financeForm.paiements_mensuels?.[cle] || {}),
                prorata: true,
                date_entree: dateRetour
              }
            }
          };
        })()
      };

      const response = await api.reinscription.create(payload);

      if (!response.success) {
        const apiError = new Error(response.message || 'La reinscription a echoue');
        apiError.code = response.code;
        apiError.status = response.status;
        apiError.data = response.data;
        throw apiError;
      }

      setToastInfo({ type: 'success', message: response.message || 'Réinscription effectuée avec succès' });
      resetFlow();
      await refreshContext();
    } catch (error) {
      const message = error.message || 'Erreur lors de la reinscription';
      const isSeuilError = error.code === 'CLASSE_SEUIL_ATTEINT' || /seuil/i.test(message);
      setToastInfo({
        type: 'error',
        message,
        long: isSeuilError,
        title: isSeuilError ? 'Seuil de classe atteint' : undefined
      });
    } finally {
      setSubmitting(false);
    }
  };


  const renderToast = () => {
    if (!toastInfo) return null;

    if (shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo)) {
      return (
        <LongToast
          show
          title={toastInfo.title}
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={() => setToastInfo(null)}
        />
      );
    }

    return (
      <Toast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo(null)}
      />
    );
  };

  const renderNoPreviousYear = () => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
      <div className="flex gap-4">
        <div className="w-11 h-11 rounded-lg bg-white text-amber-600 flex items-center justify-center flex-shrink-0">
          <AlertCircle size={22} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-amber-900">Aucune source de reinscription disponible</h3>
          <p className="mt-2 text-sm text-amber-800 leading-6">
            La reinscription utilise une annee source et une annee cible preparee. Preparez d'abord la structure
            pedagogique et le calendrier financier de l'annee cible depuis la cloture annuelle.
          </p>
        </div>
      </div>
    </div>
  );

  const renderReadOnlyYearBlock = () => (
    <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-6">
      <div className="flex gap-4">
        <div className="w-11 h-11 rounded-lg bg-white text-[#246990] flex items-center justify-center flex-shrink-0">
          <AlertCircle size={22} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#246990]">Réinscription indisponible sur une année archivée</h3>
          <p className="mt-2 text-sm text-gray-700 leading-6">
            Vous consultez l'année {contextBlock?.currentYear?.nom || 'archivée'} en lecture seule. La réinscription
            se lance depuis l'année active {contextBlock?.activeYear?.nom || '-'}, avec l'année clôturée comme source.
          </p>
          <p className="mt-2 text-sm text-gray-600 leading-6">
            Pour réinscrire un élève, reconnectez-vous sur l'année active puis utilisez cette page. Ici, aucune recherche
            ni création de réinscription n'est autorisée.
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep1Draft = () => {
    const totalPages = Math.max(1, Math.ceil(candidates.length / CANDIDATE_PAGE_SIZE));
    const safePage = Math.min(candidatePage, totalPages);
    const pageStart = (safePage - 1) * CANDIDATE_PAGE_SIZE;
    const visibleCandidates = candidates.slice(pageStart, pageStart + CANDIDATE_PAGE_SIZE);
    const familyTotalPages = Math.max(1, Math.ceil(familyCandidates.length / CANDIDATE_PAGE_SIZE));
    const familySafePage = Math.min(familyPage, familyTotalPages);
    const familyPageStart = (familySafePage - 1) * CANDIDATE_PAGE_SIZE;
    const visibleFamilies = familyCandidates.slice(familyPageStart, familyPageStart + CANDIDATE_PAGE_SIZE);

    return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Année source</label>
          <SearchableDropdown
            value={selectedYearId}
            options={yearOptions}
            onChange={(option) => {
              setSelectedYearId(String(option.value));
              resetFlow();
              setSelectedYearId(String(option.value));
            }}
            placeholder="Choisir une annee source"
            emptyLabel="Aucune annee source"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Rechercher un élève</label>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Nom, prénom ou matricule..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#cfe3ec] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h3 className="font-bold text-[#246990]">Reinscription groupee famille</h3>
            <p className="text-sm text-gray-600">Rechercher une famille source, choisir les membres a reprendre, puis valider en une seule action.</p>
          </div>
          {familySearchLoading && <Loader2 className="animate-spin text-[#246990]" size={20} />}
        </div>

        <div className="p-5 space-y-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={familySearchTerm}
              onChange={(event) => setFamilySearchTerm(event.target.value)}
              placeholder="Rechercher une famille ou un membre..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
            />
          </div>

          {familyCandidates.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="divide-y divide-gray-100">
                {visibleFamilies.map((famille) => {
                  const loadingFamily = bulkLoadingFamilyId === famille.id;
                  return (
                    <button
                      key={famille.id}
                      type="button"
                      onClick={() => loadFamilyDetails(famille)}
                      disabled={bulkSubmitting || bulkLoadingFamilyId !== null}
                      className="w-full px-4 py-3 text-left hover:bg-[#f4fafc] disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 truncate">{famille.nom}</p>
                        <p className="text-xs text-gray-500">
                          {famille.total_membres} membre(s) source · {famille.deja_reinscrits} deja repris · {famille.a_reprendre} a reprendre
                        </p>
                      </div>
                      <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold inline-flex items-center gap-2">
                        {loadingFamily && <Loader2 size={13} className="animate-spin" />}
                        Ouvrir
                      </span>
                    </button>
                  );
                })}
              </div>

              {familyCandidates.length > CANDIDATE_PAGE_SIZE && (
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">
                    {familyPageStart + 1}-{Math.min(familyPageStart + CANDIDATE_PAGE_SIZE, familyCandidates.length)} sur {familyCandidates.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={familySafePage <= 1} onClick={() => setFamilyPage((page) => Math.max(1, page - 1))} className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                      Precedent
                    </button>
                    <span className="text-xs font-semibold text-[#246990]">Page {familySafePage}/{familyTotalPages}</span>
                    <button type="button" disabled={familySafePage >= familyTotalPages} onClick={() => setFamilyPage((page) => Math.min(familyTotalPages, page + 1))} className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkFamilyDossier?.famille?.source && (
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase font-semibold text-[#246990]">Famille selectionnee</p>
                  <h4 className="font-bold text-gray-900">{bulkFamilyDossier.famille.source.nom}</h4>
                </div>
                <button type="button" onClick={() => setBulkFamilyDossier(null)} className="p-2 rounded-md text-gray-500 hover:bg-white">
                  <X size={16} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600">Membres a reprendre</div>
                    <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                      {(bulkFamilyDossier.famille.membres || []).map((member) => {
                        const selected = bulkMemberIds.includes(member.id);
                        return (
                          <div key={member.id} className="p-3 space-y-2">
                            <label className="flex items-center justify-between gap-3 cursor-pointer">
                              <span className="min-w-0">
                                <span className="block font-semibold text-gray-900 truncate">{member.prenom} {member.nom}</span>
                                <span className="block text-xs text-gray-500">{member.matricule || 'Sans matricule'} · {member.classe_nom || 'Classe inconnue'}</span>
                              </span>
                              {member.deja_reinscrit ? (
                                <span className="px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-semibold">Deja repris</span>
                              ) : (
                                <input type="checkbox" checked={selected} onChange={() => toggleBulkMember(member.id)} className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]" />
                              )}
                            </label>
                            {selected && !member.deja_reinscrit && (
                              <div className="space-y-2">
                                <GroupedClassDropdown
                                  value={bulkClassByMember[member.id] || ''}
                                  classes={classes}
                                  sections={sections}
                                  onChange={(classe) => selectBulkClass(member.id, classe.id)}
                                />
                                <ClassCapacityInfo {...getClassCapacityInfoProps(bulkClassByMember[member.id])} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3 space-y-3">
                      <p className="text-xs uppercase font-semibold text-[#246990]">Reductions famille</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => {
                          const source = bulkFamilyDossier.famille.source;
                          setFamilyReductionMode('source');
                          setFamilyReductions({
                            reduction_inscription: toMoneyNumber(source.reduction_inscription),
                            reduction_mensuel: toMoneyNumber(source.reduction_mensuel),
                            description: source.description || ''
                          });
                        }} className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'source' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}>
                          Anciennes reductions
                        </button>
                        <button type="button" onClick={() => setFamilyReductionMode('custom')} className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'custom' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}>
                          Ajuster
                        </button>
                      </div>
                      <label className="text-xs font-semibold text-gray-600 block">
                        Reduction reinscription
                        <input type="number" min="0" value={familyReductions.reduction_inscription} onChange={(event) => updateFamilyReduction('reduction_inscription', event.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                      </label>
                      <label className="text-xs font-semibold text-gray-600 block">
                        Reduction mensuelle
                        <input type="number" min="0" value={familyReductions.reduction_mensuel} onChange={(event) => updateFamilyReduction('reduction_mensuel', event.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                      </label>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                      <p className="text-xs uppercase font-semibold text-gray-600">Paiements groupés</p>
                      <label className="flex items-center justify-between gap-3 text-sm font-semibold text-gray-800">
                        Payer les frais de reinscription
                        <input type="checkbox" checked={bulkPayReinscription} onChange={(event) => setBulkPayReinscription(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]" />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {(bulkFamilyDossier.targetMonths || []).map((month) => (
                          <label key={month.id} className="flex items-center gap-2 text-xs font-semibold text-gray-700 rounded-md border border-gray-200 px-2 py-2">
                            <input type="checkbox" checked={bulkPaidMonthIds.includes(month.id)} onChange={() => toggleBulkMonth(month.id)} className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]" />
                            {month.nom}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-600">
                    {bulkMemberIds.length} membre(s) selectionne(s)
                    {bulkProgress ? ` · ${bulkProgress}` : ''}
                  </p>
                  <button
                    type="button"
                    onClick={handleBulkFamilySubmit}
                    disabled={bulkSubmitting || bulkMemberIds.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5] disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {bulkSubmitting && <Loader2 size={16} className="animate-spin" />}
                    Valider la famille
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Élèves trouvés</h3>
            <p className="text-sm text-gray-500">
              {selectedYear ? `Source : ${selectedYear.nom}` : 'Selectionnez une annee source'}
            </p>
          </div>
          {searchLoading && <Loader2 className="animate-spin text-[#246990]" size={20} />}
        </div>

        {candidates.length === 0 ? (
          <div className="px-5 py-12 text-center text-gray-500">
            Aucun élève disponible pour cette recherche.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleCandidates.map((candidate) => {
              const isLoadingCandidate = selectedCandidateLoadingId === candidate.id;
              return (
              <button
                key={candidate.id}
                type="button"
                disabled={candidate.deja_reinscrit || detailsLoading}
                onClick={() => loadCandidateDetails(candidate)}
                className="w-full px-5 py-4 text-left hover:bg-[#f4fafc] disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[#e7f3f8] text-[#246990] flex items-center justify-center font-bold flex-shrink-0">
                    {(candidate.prenom?.[0] || candidate.nom?.[0] || 'E').toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{candidate.prenom} {candidate.nom}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {candidate.matricule || 'Sans matricule'} · {candidate.classe_nom || 'Classe inconnue'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {famillesOuvertes && candidate.famille_nom && (
                    <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold">
                      {candidate.famille_nom}
                    </span>
                  )}
                  {candidate.deja_reinscrit ? (
                    <span className="px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-xs font-semibold">
                      Déjà réinscrit
                    </span>
                  ) : isLoadingCandidate ? (
                    <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold inline-flex items-center gap-2">
                      <Loader2 size={13} className="animate-spin" />
                      Chargement
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-semibold">
                      Sélectionner
                    </span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        )}

        {candidates.length > CANDIDATE_PAGE_SIZE && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {pageStart + 1}-{Math.min(pageStart + CANDIDATE_PAGE_SIZE, candidates.length)} sur {candidates.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setCandidatePage((page) => Math.max(1, page - 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Precedent
              </button>
              <span className="text-xs font-semibold text-[#246990]">Page {safePage}/{totalPages}</span>
              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() => setCandidatePage((page) => Math.min(totalPages, page + 1))}
                className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderStep1 = () => {
    const totalPages = Math.max(1, Math.ceil(candidates.length / CANDIDATE_PAGE_SIZE));
    const safePage = Math.min(candidatePage, totalPages);
    const pageStart = (safePage - 1) * CANDIDATE_PAGE_SIZE;
    const visibleCandidates = candidates.slice(pageStart, pageStart + CANDIDATE_PAGE_SIZE);
    const familyTotalPages = Math.max(1, Math.ceil(familyCandidates.length / CANDIDATE_PAGE_SIZE));
    const familySafePage = Math.min(familyPage, familyTotalPages);
    const familyPageStart = (familySafePage - 1) * CANDIDATE_PAGE_SIZE;
    const visibleFamilies = familyCandidates.slice(familyPageStart, familyPageStart + CANDIDATE_PAGE_SIZE);

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Année source</label>
            <SearchableDropdown
              value={selectedYearId}
              options={yearOptions}
              onChange={(option) => {
                setSelectedYearId(String(option.value));
                resetFlow();
                setSelectedYearId(String(option.value));
              }}
              placeholder="Choisir une année source"
              emptyLabel="Aucune année source"
            />
          </div>

          {/* Un seul mode disponible n'est pas un choix : le bloc entier
              disparaît plutôt que de proposer une case unique. */}
          {famillesOuvertes && (
          <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4">
            <p className="text-xs font-semibold uppercase text-[#246990]">Mode de réinscription</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setFlowMode('individual');
                  setBulkFamilyDossier(null);
                  setBulkMemberIds([]);
                }}
                className={`rounded-lg border p-4 text-left transition ${flowMode === 'individual' ? 'border-[#246990] bg-white shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <UserRound size={18} className="text-[#246990]" />
                  Élève seul
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Reprendre un dossier élève avec son parcours complet.
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setFlowMode('family');
                  setDossier(null);
                }}
                className={`rounded-lg border p-4 text-left transition ${flowMode === 'family' ? 'border-[#246990] bg-white shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <Users size={18} className="text-[#246990]" />
                  Famille complète
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Choisir une famille, contrôler les membres, puis valider en fin de parcours.
                </span>
              </button>
            </div>
          </div>
          )}
        </div>

        {flowMode === 'family' ? (
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900">Familles trouvées</h3>
                <p className="text-sm text-gray-500">
                  {selectedYear ? `Source : ${selectedYear.nom}. La famille sera contrôlée sur les étapes suivantes.` : 'Sélectionnez une année source'}
                </p>
              </div>
              {familySearchLoading && <Loader2 className="animate-spin text-[#246990]" size={20} />}
            </div>

            <div className="p-5 border-b border-gray-100">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={familySearchTerm}
                  onChange={(event) => setFamilySearchTerm(event.target.value)}
                  placeholder="Rechercher une famille ou un membre..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                />
              </div>
            </div>

            {familyCandidates.length === 0 ? (
              <div className="px-5 py-12 text-center text-gray-500">
                Aucune famille disponible pour cette recherche.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {visibleFamilies.map((famille) => {
                  const loadingFamily = bulkLoadingFamilyId === famille.id;
                  return (
                    <button
                      key={famille.id}
                      type="button"
                      onClick={() => loadFamilyDetails(famille)}
                      disabled={bulkSubmitting || bulkLoadingFamilyId !== null}
                      className="w-full px-5 py-4 text-left hover:bg-[#f4fafc] disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-full bg-[#e7f3f8] text-[#246990] flex items-center justify-center font-bold flex-shrink-0">
                          {(famille.nom?.[0] || 'F').toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 truncate">{famille.nom}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {famille.total_membres} membre(s) source · {famille.deja_reinscrits} déjà repris · {famille.a_reprendre} à reprendre
                          </p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold inline-flex items-center gap-2">
                        {loadingFamily && <Loader2 size={13} className="animate-spin" />}
                        Préparer
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {familyCandidates.length > CANDIDATE_PAGE_SIZE && (
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {familyPageStart + 1}-{Math.min(familyPageStart + CANDIDATE_PAGE_SIZE, familyCandidates.length)} sur {familyCandidates.length}
                </p>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={familySafePage <= 1} onClick={() => setFamilyPage((page) => Math.max(1, page - 1))} className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                    Précédent
                  </button>
                  <span className="text-xs font-semibold text-[#246990]">Page {familySafePage}/{familyTotalPages}</span>
                  <button type="button" disabled={familySafePage >= familyTotalPages} onClick={() => setFamilyPage((page) => Math.min(familyTotalPages, page + 1))} className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900">Élèves trouvés</h3>
                <p className="text-sm text-gray-500">
                  {selectedYear ? `Source : ${selectedYear.nom}` : 'Sélectionnez une année source'}
                </p>
              </div>
              {searchLoading && <Loader2 className="animate-spin text-[#246990]" size={20} />}
            </div>

            <div className="p-5 border-b border-gray-100">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Nom, prénom ou matricule..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                />
              </div>
            </div>

            {candidates.length === 0 ? (
              <div className="px-5 py-12 text-center text-gray-500">
                Aucun élève disponible pour cette recherche.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {visibleCandidates.map((candidate) => {
                  const isLoadingCandidate = selectedCandidateLoadingId === candidate.id;
                  return (
                    <button
                      key={candidate.id}
                      type="button"
                      disabled={candidate.deja_reinscrit || detailsLoading}
                      onClick={() => loadCandidateDetails(candidate)}
                      className="w-full px-5 py-4 text-left hover:bg-[#f4fafc] disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-full bg-[#e7f3f8] text-[#246990] flex items-center justify-center font-bold flex-shrink-0">
                          {(candidate.prenom?.[0] || candidate.nom?.[0] || 'E').toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 truncate">{candidate.prenom} {candidate.nom}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {candidate.matricule || 'Sans matricule'} · {candidate.classe_nom || 'Classe inconnue'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {famillesOuvertes && candidate.famille_nom && (
                          <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold">
                            {candidate.famille_nom}
                          </span>
                        )}
                        {candidate.deja_reinscrit ? (
                          <span className="px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-xs font-semibold">
                            Déjà réinscrit
                          </span>
                        ) : isLoadingCandidate ? (
                          <span className="px-2.5 py-1 rounded-md bg-[#e7f3f8] text-[#246990] text-xs font-semibold inline-flex items-center gap-2">
                            <Loader2 size={13} className="animate-spin" />
                            Chargement
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-semibold">
                            Sélectionner
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {candidates.length > CANDIDATE_PAGE_SIZE && (
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  {pageStart + 1}-{Math.min(pageStart + CANDIDATE_PAGE_SIZE, candidates.length)} sur {candidates.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setCandidatePage((page) => Math.max(1, page - 1))}
                    className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                  <span className="text-xs font-semibold text-[#246990]">Page {safePage}/{totalPages}</span>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setCandidatePage((page) => Math.min(totalPages, page + 1))}
                    className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFamilyStep2 = () => {
    const family = bulkFamilyDossier?.famille?.source;
    if (!family) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          Sélectionnez d'abord une famille à réinscrire.
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4">
          <p className="text-sm text-[#246990] font-semibold">Famille source</p>
          <p className="mt-1 text-lg font-bold text-gray-900">{family.nom}</p>
          <p className="text-sm text-gray-600">
            {bulkFamilyMembers.length} membre(s) en {selectedYear?.nom || 'année source'} · {selectedBulkMembers.length} à reprendre maintenant
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">Membres à réinscrire</h3>
            <p className="text-sm text-gray-500">Cochez uniquement les élèves que vous voulez reconduire dans la nouvelle année.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {bulkFamilyMembers.map((member) => {
              const selected = bulkMemberIds.includes(member.id);
              return (
                <div key={member.id} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-[#e7f3f8] text-[#246990] flex items-center justify-center font-bold flex-shrink-0">
                      {(member.prenom?.[0] || member.nom?.[0] || 'E').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 truncate">{member.prenom} {member.nom}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {member.matricule || 'Sans matricule'} · {member.classe_nom || 'Classe inconnue'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {member.sexe || 'Sexe non renseigné'} · {member.date_naissance ? formatInputDate(member.date_naissance) : 'Date de naissance non renseignée'}
                      </p>
                    </div>
                  </div>
                  {member.deja_reinscrit ? (
                    <span className="px-2.5 py-1 rounded-md bg-green-50 text-green-700 text-xs font-semibold">Déjà repris</span>
                  ) : (
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#246990]">
                      <input type="checkbox" checked={selected} onChange={() => toggleBulkMember(member.id)} className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]" />
                      Reprendre
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderFamilyStep3Legacy = () => {
    const family = bulkFamilyDossier?.famille?.source;
    const feeRows = selectedBulkMembers.map((member) => {
      const targetClass = classes.find((classe) => String(classe.id) === String(bulkClassByMember[member.id]));
      const normalReinscription = toMoneyNumber(targetClass?.frais_reinscription || targetClass?.frais_inscription);
      const normalMonthly = toMoneyNumber(targetClass?.frais_mensuel);
      const reductionReinscription = Math.min(normalReinscription, toMoneyNumber(familyReductions.reduction_inscription));
      const reductionMensuel = Math.min(normalMonthly, toMoneyNumber(familyReductions.reduction_mensuel));
      return {
        member,
        targetClass,
        normalReinscription,
        normalMonthly,
        reductionReinscription,
        reductionMensuel,
        reinscription: Math.max(0, normalReinscription - reductionReinscription),
        mensuel: Math.max(0, normalMonthly - reductionMensuel)
      };
    });
    const totals = feeRows.reduce((acc, row) => ({
      normalReinscription: acc.normalReinscription + row.normalReinscription,
      normalMonthly: acc.normalMonthly + row.normalMonthly,
      reductionReinscription: acc.reductionReinscription + row.reductionReinscription,
      reductionMensuel: acc.reductionMensuel + row.reductionMensuel,
      reinscription: acc.reinscription + row.reinscription,
      mensuel: acc.mensuel + row.mensuel
    }), {
      normalReinscription: 0,
      normalMonthly: 0,
      reductionReinscription: 0,
      reductionMensuel: 0,
      reinscription: 0,
      mensuel: 0
    });

    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <GraduationCap size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Classes de réinscription</h3>
          </div>
          <div className="p-5 space-y-4">
            {selectedBulkMembers.map((member) => (
              <div key={member.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="font-bold text-gray-900">{member.prenom} {member.nom}</p>
                    <p className="text-sm text-gray-500">Ancienne classe : {member.classe_nom || 'Non renseignée'}</p>
                  </div>
                  <div className="w-full md:w-72">
                    <GroupedClassDropdown
                      value={bulkClassByMember[member.id] || ''}
                      classes={classes}
                      sections={sections}
                      onChange={(classe) => selectBulkClass(member.id, classe.id)}
                    />
                    <ClassCapacityInfo {...getClassCapacityInfoProps(bulkClassByMember[member.id])} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4 space-y-3">
            <p className="text-xs uppercase font-semibold text-[#246990]">Réductions famille</p>
            <p className="font-bold text-gray-900">{family?.nom || 'Famille source'}</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => {
                setFamilyReductionMode('source');
                setFamilyReductions({
                  reduction_inscription: toMoneyNumber(family?.reduction_inscription),
                  reduction_mensuel: toMoneyNumber(family?.reduction_mensuel),
                  description: family?.description || ''
                });
              }} className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'source' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}>
                Anciennes réductions
              </button>
              <button type="button" onClick={() => setFamilyReductionMode('custom')} className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'custom' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}>
                Ajuster
              </button>
            </div>
            <label className="text-xs font-semibold text-gray-600 block">
              Réduction réinscription
              <input type="number" min="0" value={familyReductions.reduction_inscription} onChange={(event) => updateFamilyReduction('reduction_inscription', event.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </label>
            <label className="text-xs font-semibold text-gray-600 block">
              Réduction mensuelle
              <input type="number" min="0" value={familyReductions.reduction_mensuel} onChange={(event) => updateFamilyReduction('reduction_mensuel', event.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </label>
          </div>

          <div className="rounded-lg bg-white border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">Frais réels</p>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>Réinscription</span><strong>{formatAmount(totals.normalReinscription)}</strong></div>
              <div className="flex justify-between"><span>Mensuel</span><strong>{formatAmount(totals.normalMonthly)}</strong></div>
            </div>
          </div>
          <div className="rounded-lg bg-[#f4fafc] border border-[#cfe3ec] p-4">
            <p className="text-xs font-semibold text-[#246990] uppercase">Frais recalculés</p>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>Réinscription</span><strong>{formatAmount(totals.reinscription)}</strong></div>
              <div className="flex justify-between"><span>Mensuel</span><strong>{formatAmount(totals.mensuel)}</strong></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-[#f4fafc] flex items-center gap-2">
            <FileText size={18} className="text-[#246990]" />
            <div>
              <h3 className="font-bold text-gray-900">Detail des frais par membre</h3>
              <p className="text-sm text-gray-500">Les montants sont calcules par eleve, puis totalises pour la famille.</p>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {feeRows.map((row) => (
              <div key={row.member.id} className="p-5">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="font-bold text-gray-900">{row.member.prenom} {row.member.nom}</p>
                    <p className="text-sm text-gray-500">
                      {row.member.classe_nom || 'Ancienne classe inconnue'} - {row.targetClass?.nom || 'Classe cible non choisie'}
                    </p>
                  </div>
                  <span className="w-fit rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-2.5 py-1 text-xs font-semibold text-[#246990]">
                    Calcul individuel
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Frais reels</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(row.normalReinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(row.normalMonthly)}</strong></div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Reduction appliquee</p>
                    <div className="mt-2 space-y-1 text-sm text-emerald-800">
                      <div className="flex justify-between gap-3"><span>Reinscription</span><strong>- {formatAmount(row.reductionReinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>- {formatAmount(row.reductionMensuel)}</strong></div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-[#246990]">A payer</p>
                    <div className="mt-2 space-y-1 text-sm text-[#1d526f]">
                      <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(row.reinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(row.mensuel)}</strong></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="p-5 bg-slate-50">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Total frais reels</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(totals.normalReinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(totals.normalMonthly)}</strong></div>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Total reductions</p>
                  <div className="mt-2 space-y-1 text-sm text-emerald-800">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>- {formatAmount(totals.reductionReinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>- {formatAmount(totals.reductionMensuel)}</strong></div>
                  </div>
                </div>
                <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#246990]">Total a payer</p>
                  <div className="mt-2 space-y-1 text-sm text-[#1d526f]">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(totals.reinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(totals.mensuel)}</strong></div>
                  </div>
                </div>
              </div>
              <p className="mt-3 rounded-md border border-[#cfe3ec] bg-white px-3 py-2 text-xs leading-5 text-gray-600">
                La reduction famille reste appliquee dossier par dossier. Les totaux servent uniquement a comprendre la charge globale de la famille.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFamilyStep3 = () => {
    const family = bulkFamilyDossier?.famille?.source;
    const sourceTotals = bulkFamilyDossier?.famille?.stats?.source_reductions || {};
    const politiqueLaisseeTotale = {
      inscription: toMoneyNumber(sourceTotals.politique_non_reprise_inscription),
      mensuel: toMoneyNumber(sourceTotals.politique_non_reprise_mensuel)
    };
    const referenceReductionInscription = toMoneyNumber(family?.reduction_inscription);
    const referenceReductionMensuel = toMoneyNumber(family?.reduction_mensuel);
    const getSourceFeeLabel = (historique = {}) => (
      historique.libelle_frais_source
      || (historique.type_operation_source === 'reinscription'
        ? 'Frais de reinscription source'
        : "Frais d'inscription source")
    );
    const getSourceReductionLabel = (historique = {}) => (
      historique.libelle_reduction_source
      || (historique.type_operation_source === 'reinscription'
        ? 'Reduction reinscription source'
        : 'Reduction inscription source')
    );
    const getSourcePayableLabel = (historique = {}) => (
      historique.libelle_a_payer_source
      || (historique.type_operation_source === 'reinscription'
        ? 'Reinscription payee source'
        : 'Inscription payee source')
    );

    if (!family) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          Selectionnez d'abord une famille a reinscrire.
        </div>
      );
    }

    const feeRows = selectedBulkMembers.map((member) => {
      const targetClass = classes.find((classe) => String(classe.id) === String(bulkClassByMember[member.id]));
      const normalReinscription = toMoneyNumber(targetClass?.frais_reinscription || targetClass?.frais_inscription);
      const normalMonthly = toMoneyNumber(targetClass?.frais_mensuel);
      const choice = getMemberReductionChoice(member);
      const reductionReinscription = Math.min(normalReinscription, toMoneyNumber(choice.reduction_inscription));
      const reductionMensuel = Math.min(normalMonthly, toMoneyNumber(choice.reduction_mensuel));
      const historique = member.reduction_historique || {};

      return {
        member,
        targetClass,
        choice,
        historique,
        politiqueLaissee: politiqueNonReprise(historique),
        sourceFeeLabel: getSourceFeeLabel(historique),
        sourceReductionLabel: getSourceReductionLabel(historique),
        sourcePayableLabel: getSourcePayableLabel(historique),
        normalReinscription,
        normalMonthly,
        reductionReinscription,
        reductionMensuel,
        reinscription: Math.max(0, normalReinscription - reductionReinscription),
        mensuel: Math.max(0, normalMonthly - reductionMensuel)
      };
    });

    const totals = feeRows.reduce((acc, row) => ({
      normalReinscription: acc.normalReinscription + row.normalReinscription,
      normalMonthly: acc.normalMonthly + row.normalMonthly,
      reductionReinscription: acc.reductionReinscription + row.reductionReinscription,
      reductionMensuel: acc.reductionMensuel + row.reductionMensuel,
      reinscription: acc.reinscription + row.reinscription,
      mensuel: acc.mensuel + row.mensuel
    }), {
      normalReinscription: 0,
      normalMonthly: 0,
      reductionReinscription: 0,
      reductionMensuel: 0,
      reinscription: 0,
      mensuel: 0
    });
    const sourceFeeLabels = Array.from(new Set(feeRows.map((row) => row.sourceFeeLabel).filter(Boolean)));
    const sourceReductionLabels = Array.from(new Set(feeRows.map((row) => row.sourceReductionLabel).filter(Boolean)));
    const sourcePayableLabels = Array.from(new Set(feeRows.map((row) => row.sourcePayableLabel).filter(Boolean)));
    const sourceTotalFeeLabel = sourceFeeLabels.length === 1 ? sourceFeeLabels[0] : "Frais d'entree source";
    const sourceTotalReductionLabel = sourceReductionLabels.length === 1 ? sourceReductionLabels[0] : "Reduction d'entree source";
    const sourceTotalPayableLabel = sourcePayableLabels.length === 1 ? sourcePayableLabels[0] : "Entree payee source";

    const strategyButtonClass = (strategy) => (
      `group rounded-lg border px-4 py-3 text-left transition ${bulkReductionStrategy === strategy
        ? 'border-[#246990] bg-[#f4fafc] text-[#1d526f] shadow-sm ring-1 ring-[#246990]/20'
        : 'border-gray-200 bg-white text-gray-700 hover:border-[#b8d4e2] hover:bg-[#f8fbfc]'}`
    );

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
          <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
              <GraduationCap size={18} className="text-[#246990]" />
              <div>
                <h3 className="font-bold text-gray-900">Classes de reinscription</h3>
                <p className="text-sm text-gray-500">La classe suivante est proposee quand elle existe sans variante. Vous pouvez forcer un redoublement.</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {selectedBulkMembers.map((member) => {
                const promotionClass = findPromotionTargetClass(member);
                const sameClass = findEquivalentTargetClass(member);
                const selectedClass = classes.find((classe) => String(classe.id) === String(bulkClassByMember[member.id]));

                return (
                  <div key={member.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 truncate">{member.prenom} {member.nom}</p>
                        <p className="text-sm text-gray-500">
                          {member.classe_nom || 'Classe source inconnue'} vers {selectedClass?.nom || 'classe cible a choisir'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={!promotionClass}
                            onClick={() => promotionClass && selectBulkClass(member.id, promotionClass.id)}
                            className="rounded-md border border-[#b8d4e2] bg-[#f4fafc] px-2.5 py-1 text-xs font-semibold text-[#246990] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Classe suivante
                          </button>
                          <button
                            type="button"
                            disabled={!sameClass}
                            onClick={() => sameClass && selectBulkClass(member.id, sameClass.id)}
                            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Redoublement
                          </button>
                        </div>
                      </div>
                      <div className="w-full lg:w-72">
                        <GroupedClassDropdown
                          value={bulkClassByMember[member.id] || ''}
                          classes={classes}
                          sections={sections}
                          onChange={(classe) => selectBulkClass(member.id, classe.id)}
                        />
                        <ClassCapacityInfo {...getClassCapacityInfoProps(bulkClassByMember[member.id])} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4 h-fit">
            <p className="text-xs uppercase font-semibold text-[#246990]">Famille source</p>
            <p className="mt-1 font-bold text-gray-900">{family.nom}</p>
            <p className="mt-1 text-sm text-gray-600">
              {bulkFamilyMembers.length} membre(s) en {selectedYear?.nom || 'annee source'}.
            </p>
            <div className="mt-4 rounded-lg border border-[#cfe3ec] bg-white p-3 text-sm">
              <p className="text-xs font-bold uppercase text-[#246990]">Reduction de reference</p>
              <div className="mt-2 flex justify-between gap-3">
                <span>Inscription / entree</span>
                <strong>{formatAmount(referenceReductionInscription)}</strong>
              </div>
              <div className="mt-1 flex justify-between gap-3">
                <span>Mensuel</span>
                <strong>{formatAmount(referenceReductionMensuel)}</strong>
              </div>
              <p className="mt-3 text-xs leading-5 text-gray-500">
                Cette reference vient de la famille source. Elle ne remplace pas les reductions reelles deja appliquees a chaque eleve.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-[#f4fafc]">
            <h3 className="font-bold text-gray-900">Lecture et choix des reductions</h3>
            <p className="text-sm text-gray-500">On lit d'abord ce qui existait vraiment dans l'annee source, puis on choisit ce qui sera applique dans la nouvelle annee.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-5 border-b border-gray-100 lg:border-b-0 lg:border-r">
              <p className="text-xs font-bold uppercase text-[#246990]">1. Situation de l'annee source</p>
              <p className="mt-1 text-sm text-gray-600">Voici les frais et reductions réellement appliques sur chaque membre avant la cloture.</p>
              <div className="mt-4 space-y-3">
                {bulkFamilyMembers.map((member) => {
                  const historique = member.reduction_historique || {};
                  const sourceFeeLabel = getSourceFeeLabel(historique);
                  const sourceReductionLabel = getSourceReductionLabel(historique);
                  const sourcePayableLabel = getSourcePayableLabel(historique);
                  const politiqueLaissee = politiqueNonReprise(historique);
                  return (
                    <div key={member.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{member.prenom} {member.nom}</p>
                          <p className="text-xs text-gray-500">{member.classe_nom || 'Classe inconnue'} · {member.deja_reinscrit ? 'deja repris' : 'a reprendre'}</p>
                        </div>
                        <span className="rounded-md bg-[#e7f3f8] px-2 py-1 text-xs font-semibold text-[#246990]">
                          {formatSourceReductionMode(historique)}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg bg-slate-50 p-3">
                          <span className="block text-gray-500">{sourceReductionLabel}</span>
                          <strong className="text-gray-900">{formatAmount(historique.reduction_inscription)}</strong>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <span className="block text-gray-500">Reduction mensuelle</span>
                          <strong className="text-gray-900">{formatAmount(historique.reduction_mensuel)}</strong>
                        </div>
                        <div className="rounded-lg bg-[#f4fafc] p-3">
                          <span className="block text-[#246990]">Mensuel paye source</span>
                          <strong className="text-[#1d526f]">{formatAmount(historique.frais_mensuel_final)}</strong>
                        </div>
                        <div className="rounded-lg bg-[#f4fafc] p-3">
                          <span className="block text-[#246990]">{sourcePayableLabel}</span>
                          <strong className="text-[#1d526f]">{formatAmount(historique.frais_debut_annee_final ?? historique.frais_reinscription_final)}</strong>
                        </div>
                        <div className="col-span-2 rounded-lg border border-gray-100 bg-white p-3">
                          <span className="block text-gray-500">{sourceFeeLabel}</span>
                          <strong className="text-gray-900">{formatAmount(historique.frais_debut_annee_reel ?? historique.frais_reinscription_reel)}</strong>
                        </div>
                        {(politiqueLaissee.inscription > 0 || politiqueLaissee.mensuel > 0) && (
                          <div className="col-span-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-purple-800">
                            <span className="block font-semibold">Politique d'école non reprise</span>
                            <span className="mt-1 block leading-5">
                              Mérite de l'année source, non reconduit : {textePolitiqueNonReprise(politiqueLaissee)}.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3 text-sm">
                <p className="font-semibold text-[#246990]">Total source lu</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>{sourceTotalFeeLabel} : <strong>{formatAmount(sourceTotals.frais_debut_annee ?? sourceTotals.frais_reinscription)}</strong></div>
                  <div>Frais mensuels source : <strong>{formatAmount(sourceTotals.frais_mensuel)}</strong></div>
                  <div>{sourceTotalReductionLabel} : <strong>{formatAmount(sourceTotals.reduction_inscription)}</strong></div>
                  <div>Reduction mensuelle : <strong>{formatAmount(sourceTotals.reduction_mensuel)}</strong></div>
                  <div>{sourceTotalPayableLabel} : <strong>{formatAmount(sourceTotals.a_payer_debut_annee ?? sourceTotals.a_payer_reinscription)}</strong></div>
                  <div>A payer mensuel : <strong>{formatAmount(sourceTotals.a_payer_mensuel)}</strong></div>
                  {(politiqueLaisseeTotale.inscription > 0 || politiqueLaisseeTotale.mensuel > 0) && (
                    <div className="col-span-2 text-purple-800">
                      Politique d'école non reprise : <strong>{textePolitiqueNonReprise(politiqueLaisseeTotale)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-5">
              <p className="text-xs font-bold uppercase text-[#246990]">2. Regle pour la nouvelle annee</p>
              <p className="mt-1 text-sm text-gray-600">Choisissez comment transformer ces reductions sur les nouvelles classes. Le detail s'ouvre juste apres votre choix.</p>
              <div className="mt-4 grid grid-cols-1 gap-3">
                <button type="button" onClick={() => applyBulkReductionStrategy('historique')} className={strategyButtonClass('historique')}>
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block font-bold">Reprendre le reel par eleve</span>
                      <span className="mt-1 block text-xs text-gray-500">Chaque membre garde la reduction réellement constatee sur son ancien dossier, hors politique d'école : ce mérite annuel n'est pas reconduit.</span>
                    </span>
                    {bulkReductionStrategy === 'historique' && <Check size={18} className="mt-0.5 text-[#246990]" />}
                  </span>
                </button>
                <button type="button" onClick={() => applyBulkReductionStrategy('reference')} className={strategyButtonClass('reference')}>
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block font-bold">Reference famille pour tous</span>
                      <span className="mt-1 block text-xs text-gray-500">Tous les membres prennent la reduction de reference de la famille.</span>
                    </span>
                    {bulkReductionStrategy === 'reference' && <Check size={18} className="mt-0.5 text-[#246990]" />}
                  </span>
                </button>
                <button type="button" onClick={() => applyBulkReductionStrategy('personnalisee')} className={strategyButtonClass('personnalisee')}>
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block font-bold">Ajuster par eleve</span>
                      <span className="mt-1 block text-xs text-gray-500">Vous modifiez chaque reduction dossier par dossier avant validation.</span>
                    </span>
                    {bulkReductionStrategy === 'personnalisee' && <Check size={18} className="mt-0.5 text-[#246990]" />}
                  </span>
                </button>
                <button type="button" onClick={() => applyBulkReductionStrategy('aucune')} className={strategyButtonClass('aucune')}>
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block font-bold">Ne rien reprendre</span>
                      <span className="mt-1 block text-xs text-gray-500">La famille est rattachee, mais aucun montant de reduction n'est applique aux membres.</span>
                    </span>
                    {bulkReductionStrategy === 'aucune' && <Check size={18} className="mt-0.5 text-[#246990]" />}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div ref={familyFeeDetailsRef} className="scroll-mt-6 rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-[#f4fafc] flex items-center gap-2">
            <FileText size={18} className="text-[#246990]" />
            <div>
              <h3 className="font-bold text-gray-900">Detail des frais par membre</h3>
              <p className="text-sm text-gray-500">Les frais reels viennent des classes choisies pour la nouvelle annee. Les reductions restent individuelles.</p>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {feeRows.map((row) => (
              <div key={row.member.id} className="p-5">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="font-bold text-gray-900">{row.member.prenom} {row.member.nom}</p>
                    <p className="text-sm text-gray-500">
                      {row.member.classe_nom || 'Ancienne classe inconnue'} vers {row.targetClass?.nom || 'classe cible non choisie'}
                    </p>
                  </div>
                  <span className="w-fit rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-2.5 py-1 text-xs font-semibold text-[#246990]">
                    {formatReductionMode(row.choice.mode)}
                  </span>
                </div>

                {bulkReductionStrategy === 'personnalisee' && (
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-xs font-semibold text-gray-600">
                      Reduction sur frais de reinscription a appliquer
                      <input
                        type="number"
                        min="0"
                        value={row.choice.reduction_inscription}
                        onChange={(event) => updateBulkMemberReduction(row.member.id, 'reduction_inscription', event.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-600">
                      Reduction mensuelle a appliquer
                      <input
                        type="number"
                        min="0"
                        value={row.choice.reduction_mensuel}
                        onChange={(event) => updateBulkMemberReduction(row.member.id, 'reduction_mensuel', event.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                      />
                    </label>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Annee source</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><span>{row.sourceFeeLabel}</span><strong>{formatAmount(row.historique.frais_debut_annee_reel ?? row.historique.frais_reinscription_reel)}</strong></div>
                      <div className="flex justify-between gap-3"><span>{row.sourceReductionLabel}</span><strong>{formatAmount(row.historique.reduction_inscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Reduction mensuelle</span><strong>{formatAmount(row.historique.reduction_mensuel)}</strong></div>
                      <div className="flex justify-between gap-3"><span>{row.sourcePayableLabel}</span><strong>{formatAmount(row.historique.frais_debut_annee_final ?? row.historique.frais_reinscription_final)}</strong></div>
                      {row.politiqueLaissee.inscription > 0 && (
                        <div className="flex justify-between gap-3 text-purple-800"><span>Politique non reprise - entrée</span><strong>- {formatAmount(row.politiqueLaissee.inscription)}</strong></div>
                      )}
                      {row.politiqueLaissee.mensuel > 0 && (
                        <div className="flex justify-between gap-3 text-purple-800"><span>Politique non reprise - mensuel</span><strong>- {formatAmount(row.politiqueLaissee.mensuel)}</strong></div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Frais reels cible</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><span>Frais de reinscription</span><strong>{formatAmount(row.normalReinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(row.normalMonthly)}</strong></div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Reduction appliquee</p>
                    <div className="mt-2 space-y-1 text-sm text-emerald-800">
                      <div className="flex justify-between gap-3"><span>Sur reinscription</span><strong>- {formatAmount(row.reductionReinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>- {formatAmount(row.reductionMensuel)}</strong></div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-[#246990]">A payer</p>
                    <div className="mt-2 space-y-1 text-sm text-[#1d526f]">
                      <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(row.reinscription)}</strong></div>
                      <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(row.mensuel)}</strong></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="p-5 bg-slate-50">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Total frais reels cible</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(totals.normalReinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(totals.normalMonthly)}</strong></div>
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Total reductions cible</p>
                  <div className="mt-2 space-y-1 text-sm text-emerald-800">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>- {formatAmount(totals.reductionReinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>- {formatAmount(totals.reductionMensuel)}</strong></div>
                  </div>
                </div>
                <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#246990]">Total a payer cible</p>
                  <div className="mt-2 space-y-1 text-sm text-[#1d526f]">
                    <div className="flex justify-between gap-3"><span>Reinscription</span><strong>{formatAmount(totals.reinscription)}</strong></div>
                    <div className="flex justify-between gap-3"><span>Mensuel</span><strong>{formatAmount(totals.mensuel)}</strong></div>
                  </div>
                </div>
              </div>
              <p className="mt-3 rounded-md border border-[#cfe3ec] bg-white px-3 py-2 text-xs leading-5 text-gray-600">
                Le rattachement famille est commun, mais les reductions sont conservees ou ajustees eleve par eleve. Cela evite de remplacer les reductions reelles par une seule reduction globale.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFamilyStep4 = () => {
    const selectedCount = selectedBulkMembers.length;
    const feeRows = getBulkMemberTargetFees();
    const reinscriptionTotal = feeRows.reduce((sum, row) => sum + toMoneyNumber(row.reinscription), 0);
    const monthlyDueTotal = feeRows.reduce((sum, row) => sum + toMoneyNumber(row.mensuel), 0);
    const monthlyPaidTotal = bulkTargetMonths.reduce(
      (sum, month) => sum + Math.min(toMoneyNumber(bulkMonthlyAmounts[month.id]), monthlyDueTotal),
      0
    );
    const monthlyCount = bulkTargetMonths.filter((month) => toMoneyNumber(bulkMonthlyAmounts[month.id]) > 0).length;
    const totalToRecord = (bulkPayReinscription ? reinscriptionTotal : 0) + monthlyPaidTotal;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <CreditCard size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Paiements groupés</h3>
          </div>
          <div className="p-5 space-y-4">
            <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50">
              <span>
                <span className="block text-sm font-semibold text-gray-900">Payer les frais de réinscription</span>
                <span className="block text-xs text-gray-500">Le paiement sera enregistré pour chaque membre sélectionné.</span>
              </span>
              <input
                type="checkbox"
                checked={bulkPayReinscription}
                onChange={(event) => setBulkPayReinscription(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
              />
            </label>

            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h4 className="font-bold text-gray-900">Mois à payer maintenant</h4>
                <p className="text-xs text-gray-500">Saisissez le montant apporté par la famille. Il sera réparti sur les membres sélectionnés.</p>
              </div>
              <div className="p-4 space-y-3">
                <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3 text-sm text-[#1d526f]">
                  Montant mensuel attendu pour la famille : <strong>{formatAmount(monthlyDueTotal)}</strong>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  {bulkTargetMonths.map((month) => {
                    const amount = toMoneyNumber(bulkMonthlyAmounts[month.id]);
                    const isFull = monthlyDueTotal > 0 && amount >= monthlyDueTotal;
                    return (
                      <div key={month.id} className={`rounded-lg border p-3 ${amount > 0 ? 'border-[#b8d4e2] bg-[#f4fafc]' : 'border-gray-200 bg-white'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-gray-900">{month.nom}</p>
                            <p className="text-xs text-gray-500">
                              {amount > 0
                                ? `${isFull ? 'Mois complet' : 'Paiement partiel'} saisi`
                                : 'Aucun paiement saisi'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => updateBulkMonthAmount(month.id, monthlyDueTotal)}
                            className="rounded-md border border-[#b8d4e2] bg-white px-2.5 py-1 text-xs font-semibold text-[#246990] hover:bg-[#e7f3f8]"
                          >
                            Tout payer
                          </button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max={monthlyDueTotal}
                            value={amount || ''}
                            onChange={(event) => updateBulkMonthAmount(month.id, event.target.value)}
                            placeholder="0"
                            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                          />
                          <span className="text-xs font-semibold text-gray-500">FCFA</span>
                        </div>
                        {amount > monthlyDueTotal && (
                          <p className="mt-2 text-xs text-amber-700">
                            Le montant sera limite a {formatAmount(monthlyDueTotal)}.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-bold text-gray-900">Résumé</h3>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Membres</span><strong>{selectedCount}</strong></div>
            <div className="flex justify-between"><span className="text-gray-600">Frais réinscription dus</span><strong>{formatAmount(reinscriptionTotal)}</strong></div>
            <div className="flex justify-between"><span className="text-gray-600">Frais réinscription à enregistrer</span><strong>{formatAmount(bulkPayReinscription ? reinscriptionTotal : 0)}</strong></div>
            <div className="flex justify-between"><span className="text-gray-600">Mensuel attendu / mois</span><strong>{formatAmount(monthlyDueTotal)}</strong></div>
            <div className="flex justify-between"><span className="text-gray-600">Mois avec paiement</span><strong>{monthlyCount}</strong></div>
            <div className="flex justify-between"><span className="text-gray-600">Montant mensuel saisi</span><strong>{formatAmount(monthlyPaidTotal)}</strong></div>
            <div className="rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] p-3">
              <div className="flex justify-between gap-3 text-[#1d526f]">
                <span className="font-semibold">Total à enregistrer</span>
                <strong>{formatAmount(totalToRecord)}</strong>
              </div>
            </div>
            <div className="rounded-lg bg-[#f4fafc] border border-[#cfe3ec] p-3 text-xs text-gray-600 leading-5">
              Les montants mensuels saisis sont répartis automatiquement sur chaque membre au moment de la validation.
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFamilyStep5 = () => (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <ImageIcon size={18} className="text-[#246990]" />
          <h3 className="font-bold text-gray-900">Photos des élèves</h3>
        </div>
        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {selectedBulkMembers.map((member) => {
            const photoChoice = bulkPhotoByMember[member.id] || {};
            // Le chemin relatif rendu par l'API, signature comprise, jamais une adresse absolue
            // vers www : voir `sourcePhotoUrl` plus haut pour le detail de ce que l'adresse
            // absolue cassait des qu'on n'etait pas sur le domaine de www.
            const sourceUrl = member.photo_url && photoChoice.keepPhoto !== false ? member.photo_url : '';
            const preview = photoChoice.preview || sourceUrl;
            const cameraForMember = cameraActive && bulkCameraMemberId === member.id;

            return (
              <div key={member.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center text-gray-400 flex-shrink-0">
                    {preview ? (
                      <img src={preview} alt={`${member.prenom} ${member.nom}`} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900 truncate">{member.prenom} {member.nom}</p>
                    <p className="text-xs text-gray-500">{member.matricule || 'Sans matricule'} · {classes.find((classe) => String(classe.id) === String(bulkClassByMember[member.id]))?.nom || 'Classe cible non choisie'}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#246990] text-white text-xs font-semibold hover:bg-[#3084b5] cursor-pointer">
                        <Upload size={14} />
                        Insérer
                        <input type="file" accept="image/*" onChange={(event) => handleBulkPhotoFileChange(member.id, event)} className="hidden" />
                      </label>
                      <button type="button" onClick={() => startBulkPhotoCamera(member.id)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] text-[#246990] text-xs font-semibold hover:bg-[#d9edf5]">
                        <Camera size={14} />
                        Prendre
                      </button>
                      {member.photo_url && !photoChoice.dataUrl && photoChoice.keepPhoto !== false && (
                        <button type="button" onClick={() => clearBulkPhoto(member.id, false)} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50">
                          Ne pas reprendre
                        </button>
                      )}
                      {(photoChoice.dataUrl || photoChoice.keepPhoto === false) && (
                        <button type="button" onClick={() => clearBulkPhoto(member.id, true)} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50">
                          Réinitialiser
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {cameraForMember && (
                  <div className="mt-4 space-y-3">
                    <div className="mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg bg-black border border-gray-200">
                      <video ref={videoRef} autoPlay playsInline className="h-full w-full object-cover" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={captureBulkPhoto} className="px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5]">
                        Capturer
                      </button>
                      <button type="button" onClick={stopPhotoCamera} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50">
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {cameraError && (
          <div className="mx-5 mb-5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{cameraError}</div>
        )}
      </div>

      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4">
        <h3 className="font-bold text-[#246990]">Résumé avant validation</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Famille</p>
            <p className="font-bold text-gray-900">{bulkFamilyDossier?.famille?.source?.nom || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Membres</p>
            <p className="font-bold text-gray-900">{selectedBulkMembers.length}</p>
          </div>
          <div>
            <p className="text-gray-500">Réinscription</p>
            <p className="font-bold text-gray-900">{bulkPayReinscription ? 'Payée maintenant' : 'À payer plus tard'}</p>
          </div>
          <div>
            <p className="text-gray-500">Mois payés</p>
            <p className="font-bold text-gray-900">{bulkPaidMonthIds.length}</p>
          </div>
        </div>
        {bulkProgress && (
          <div className="mt-4 rounded-lg border border-[#b8d4e2] bg-white px-3 py-2 text-sm text-[#246990] flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {bulkProgress}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4">
        <p className="text-sm text-[#246990] font-semibold">Dossier source</p>
        <p className="mt-1 text-sm text-gray-700">
          {dossier?.eleve?.prenom} {dossier?.eleve?.nom} · ancienne année {dossier?.eleve?.annee_nom || selectedYear?.nom} · ancienne classe {dossier?.eleve?.classe_nom || 'Non renseignée'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <UserRound size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Identité de l'élève</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nom</label>
              <input value={studentForm.nom} onChange={(event) => setStudentForm((prev) => ({ ...prev, nom: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Prénom</label>
              <input value={studentForm.prenom} onChange={(event) => setStudentForm((prev) => ({ ...prev, prenom: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Sexe</label>
              <SearchableDropdown
                value={studentForm.sexe}
                options={sexOptions}
                onChange={(option) => setStudentForm((prev) => ({ ...prev, sexe: option.value }))}
                searchable={false}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Date de naissance</label>
              <input type="date" value={studentForm.date_naissance} onChange={(event) => setStudentForm((prev) => ({ ...prev, date_naissance: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Lieu de naissance</label>
              <input value={studentForm.lieu_naissance} onChange={(event) => setStudentForm((prev) => ({ ...prev, lieu_naissance: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nationalité</label>
              <input value={studentForm.nationalite} onChange={(event) => setStudentForm((prev) => ({ ...prev, nationalite: event.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-[#246990]" />
              <h3 className="font-bold text-gray-900">Parents / tuteurs</h3>
            </div>
            <button type="button" onClick={addParent} className="px-3 py-1.5 rounded-md bg-[#e7f3f8] text-[#246990] text-sm font-semibold border border-[#b8d4e2]">
              Ajouter
            </button>
          </div>

          <div className="p-4 space-y-4">
            {parentsForm.map((parent, parentIndex) => (
              <div key={parentIndex} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="font-semibold text-gray-900">Responsable {parentIndex + 1}</p>
                  {parentsForm.length > 1 && (
                    <button type="button" onClick={() => removeParent(parentIndex)} className="p-1.5 rounded-md text-red-600 hover:bg-red-50">
                      <X size={16} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Lien</label>
                    <SearchableDropdown
                      value={parent.relation}
                      options={parentRelationOptions}
                      onChange={(option) => updateParent(parentIndex, 'relation', option.value)}
                      searchable={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nom et prénom</label>
                    <input value={parent.nom_prenom} onChange={(event) => updateParent(parentIndex, 'nom_prenom', event.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                    <input value={parent.email} onChange={(event) => updateParent(parentIndex, 'email', event.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Adresse</label>
                    <input value={parent.adresse} onChange={(event) => updateParent(parentIndex, 'adresse', event.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent" />
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {(parent.telephones || ['']).map((phone, phoneIndex) => (
                    <div key={phoneIndex}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Téléphone {phoneIndex + 1}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            value={phone}
                            onChange={(event) => updateParentPhone(parentIndex, phoneIndex, event.target.value)}
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                          />
                        </div>
                        {(parent.telephones || []).length > 1 && (
                          <button type="button" onClick={() => removeParentPhone(parentIndex, phoneIndex)} className="px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                            <X size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {(parent.telephones || []).length < 2 && (
                    <button type="button" onClick={() => addParentPhone(parentIndex)} className="text-sm font-semibold text-[#246990] hover:underline">
                      Ajouter un second numéro
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <GraduationCap size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Classe de réinscription</h3>
          </div>

          <div className="p-4 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Classe précédente</p>
                <p className="font-bold text-gray-900">{dossier?.eleve?.classe_nom || 'Non renseignée'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Année source</p>
                <p className="font-bold text-gray-900">{dossier?.eleve?.annee_nom || selectedYear?.nom}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nouvelle classe</label>
              <GroupedClassDropdown
                value={newClassId}
                classes={classes}
                sections={sections}
                onChange={(classe) => selectNewClass(classe.id)}
              />
              <ClassCapacityInfo {...getClassCapacityInfoProps(newClassId)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  const equivalentClass = findEquivalentTargetClass(dossier?.eleve);
                  selectNewClass(equivalentClass?.id || '');
                }}
                className="px-4 py-3 rounded-lg border border-[#b8d4e2] bg-[#f4fafc] text-[#246990] text-sm font-semibold text-left hover:bg-[#e7f3f8]"
              >
                Même classe que l'année précédente
              </button>
              <button
                type="button"
                onClick={() => selectNewClass('')}
                className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold text-left hover:bg-gray-50"
              >
                Choisir une autre classe
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Règles reprises</h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Reprendre la famille suppose le module familles nombreuses. */}
            {famillesOuvertes && (
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={individualReductionMode === 'family' && resumeFamilyReductions}
                onChange={(event) => {
                  if (event.target.checked) {
                    applySourceFamilyReductions();
                  } else {
                    selectIndividualReductionMode('none');
                  }
                }}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                disabled={!sourceFamily}
              />
              <span>
                <span className="block text-sm font-semibold text-gray-900">Reprendre la famille et ses réductions</span>
                <span className="block text-xs text-gray-500">
                  {sourceFamily
                    ? `La famille ${sourceFamily.nom} sera recrée ou réutilisée dans ${targetYear?.nom || "l'année cible"}.`
                    : "Aucune famille nombreuse n'était rattachée au dossier source."}
                </span>
              </span>
            </label>
            )}

            {/* Les reductions portent sur les frais scolaires : sans
                comptabilite, ce ne serait qu une note sans effet. */}
            {comptabiliteOuverte && (
            <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
              <div>
                <p className="text-xs font-semibold text-[#246990] uppercase">Mode de réduction</p>
                <p className="text-xs text-gray-500 mt-1">
                  Même sans réduction l'année source, vous pouvez appliquer une réduction pour cette réinscription.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => selectIndividualReductionMode('none')}
                  className={`px-3 py-2 rounded-md border text-left text-xs font-semibold ${individualReductionMode === 'none' ? 'border-[#246990] bg-[#f4fafc] text-[#246990]' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Aucune réduction
                </button>
                <button
                  type="button"
                  onClick={() => selectIndividualReductionMode('exceptionnelle')}
                  className={`px-3 py-2 rounded-md border text-left text-xs font-semibold ${individualReductionMode === 'exceptionnelle' ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Réduction exceptionnelle
                </button>
                <button
                  type="button"
                  onClick={() => selectIndividualReductionMode('politique')}
                  className={`px-3 py-2 rounded-md border text-left text-xs font-semibold ${individualReductionMode === 'politique' ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Politique d'école
                </button>
              </div>
            </div>
            )}

            {sourceFamily && (
              <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-[#246990] uppercase">Famille source</p>
                  <p className="mt-1 font-bold text-gray-900">{sourceFamily.nom}</p>
                  <p className="text-xs text-gray-500">
                    {sourceFamilyStats.total_source || 0} membre(s) en {selectedYear?.nom || 'annee source'} · {sourceFamilyStats.deja_reinscrits || 0} deja repris
                  </p>
                </div>

                {individualReductionMode === 'family' && resumeFamilyReductions && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={applySourceFamilyReductions}
                      className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'source' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}
                    >
                      Anciennes reductions
                    </button>
                    <button
                      type="button"
                      onClick={() => setFamilyReductionMode('custom')}
                      className={`px-3 py-2 rounded-md border text-xs font-semibold ${familyReductionMode === 'custom' ? 'border-[#246990] bg-white text-[#246990]' : 'border-gray-200 bg-white text-gray-600'}`}
                    >
                      Ajuster
                    </button>
                  </div>
                )}

                {individualReductionMode === 'family' && resumeFamilyReductions && (
                  <div className="grid grid-cols-1 gap-2">
                    <label className="text-xs font-semibold text-gray-600">
                      Reduction reinscription
                      <input
                        type="number"
                        min="0"
                        value={familyReductions.reduction_inscription}
                        onChange={(event) => updateFamilyReduction('reduction_inscription', event.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-600">
                      Reduction mensuelle
                      <input
                        type="number"
                        min="0"
                        value={familyReductions.reduction_mensuel}
                        onChange={(event) => updateFamilyReduction('reduction_mensuel', event.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                      />
                    </label>
                  </div>
                )}
              </div>
            )}

            {individualReductionMode === 'exceptionnelle' && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Percent size={16} className="text-orange-600" />
                  <p className="text-sm font-bold text-orange-800">Réduction exceptionnelle</p>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <label className="text-xs font-semibold text-gray-700">
                    Réduction sur frais de réinscription
                    <input
                      type="number"
                      min="0"
                      max={effectiveFees.normalReinscription}
                      value={individualReductions.reduction_inscription}
                      onChange={(event) => updateIndividualReduction('reduction_inscription', event.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-md border border-orange-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-700">
                    Réduction mensuelle
                    <input
                      type="number"
                      min="0"
                      max={effectiveFees.normalMonthly}
                      value={individualReductions.reduction_mensuel}
                      onChange={(event) => updateIndividualReduction('reduction_mensuel', event.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-md border border-orange-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-700">
                    Motif
                    <textarea
                      rows={2}
                      value={individualReductions.motif}
                      onChange={(event) => updateIndividualReduction('motif', event.target.value)}
                      placeholder="Ex. décision de direction, situation sociale..."
                      className="mt-1 w-full px-3 py-2 rounded-md border border-orange-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                    />
                  </label>
                </div>
              </div>
            )}

            {individualReductionMode === 'politique' && (
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-purple-600" />
                  <p className="text-sm font-bold text-purple-800">Politique d'école</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Type de politique</label>
                  <SearchableDropdown
                    value={individualReductions.politique_type}
                    options={politiqueTypeOptions}
                    onChange={(option) => updateIndividualReduction('politique_type', option.value)}
                    searchable={false}
                    placeholder="Sélectionner une politique"
                  />
                </div>
                <div className="rounded-md border border-purple-200 bg-white px-3 py-2 text-xs text-purple-700">
                  Les frais de réinscription sont offerts automatiquement. Vous pouvez ajouter une réduction mensuelle si nécessaire.
                </div>
                <label className="text-xs font-semibold text-gray-700">
                  Réduction mensuelle
                  <input
                    type="number"
                    min="0"
                    max={effectiveFees.normalMonthly}
                    value={individualReductions.reduction_mensuel}
                    onChange={(event) => updateIndividualReduction('reduction_mensuel', event.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-md border border-purple-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-700">
                  Motif
                  <textarea
                    rows={2}
                    value={individualReductions.politique_motif}
                    onChange={(event) => updateIndividualReduction('politique_motif', event.target.value)}
                    placeholder="Ex. élève admis aux examens, dispositif de rentrée..."
                    className="mt-1 w-full px-3 py-2 rounded-md border border-purple-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  />
                </label>
              </div>
            )}

            {sourceFamilyMembers.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-600">
                  Membres de la famille source
                </div>
                <div className="max-h-36 overflow-y-auto divide-y divide-gray-100">
                  {sourceFamilyMembers.map((member) => (
                    <div key={member.id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                      <span className="font-semibold text-gray-800 truncate">{member.prenom} {member.nom}</span>
                      <span className={`px-2 py-1 rounded-md font-semibold ${member.deja_reinscrit ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                        {member.deja_reinscrit ? 'Deja repris' : 'A reprendre'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg bg-white border border-gray-200 p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase">Frais reels</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Reinscription</span>
                  <span className="font-bold text-gray-900">{formatAmount(effectiveFees.normalReinscription)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mensuel</span>
                  <span className="font-bold text-gray-900">{formatAmount(effectiveFees.normalMonthly)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-[#f4fafc] border border-[#cfe3ec] p-3">
              <p className="text-xs font-semibold text-[#246990] uppercase">Frais recalcules</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Reinscription</span>
                  <span className="font-bold text-gray-900">{formatAmount(effectiveFees.reinscription)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mensuel</span>
                  <span className="font-bold text-gray-900">{formatAmount(effectiveFees.mensuel)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * L'étape des frais, pour une école sans comptabilité.
   *
   * Même traitement qu'à l'inscription : on ne masque pas l'étape — dans un
   * parcours numéroté, une étape absente casse la numérotation — elle cesse
   * d'être un point de saisie et annonce le tarif.
   *
   * L'échéancier est créé quand même : la licence gouverne ce que l'école peut
   * faire, jamais ce que KLASYS enregistre.
   */
  const renderStep4LectureSeule = (raison = 'licence') => {
    const mensuel = effectiveFees.normalMonthly;
    const reinscription = effectiveFees.normalReinscription;
    const nbMois = moisScolaires.length;
    const totalAnnuel = reinscription + mensuel * nbMois;

    const Ligne = ({ libelle, detail, montant, fort = false }) => (
      <div className="flex items-baseline justify-between gap-4 py-3">
        <div>
          <div className={fort ? 'font-semibold text-gray-900' : 'text-gray-700'}>{libelle}</div>
          {detail && <div className="text-xs text-gray-500 mt-0.5">{detail}</div>}
        </div>
        <div className={`shrink-0 tabular-nums ${fort ? 'text-xl font-bold text-[#246990]' : 'text-lg font-semibold text-gray-900'}`}>
          {formatAmount(montant)}
        </div>
      </div>
    );

    return (
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Frais de scolarité</h3>
          <p className="text-sm text-gray-500">
            Tarif de la classe {selectedClass?.nom ? `« ${selectedClass.nom} »` : 'choisie'}
            {targetYear?.nom ? ` pour l'année ${targetYear.nom}` : ''}.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 px-4">
          <Ligne libelle="Frais de réinscription" montant={reinscription} />
          <Ligne
            libelle="Frais mensuels"
            detail={`${nbMois} mois sur l'année scolaire`}
            montant={mensuel}
          />
          <Ligne
            libelle="Total pour l'année"
            detail="Réinscription et mensualités cumulées"
            montant={totalAnnuel}
            fort
          />
        </div>

        {raison === 'habilitation' ? (
          <BandeauCaisseReservee />
        ) : raison === 'caisse' ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="leading-relaxed">
              <strong>Votre caisse du jour n'est pas ouverte</strong> : aucun encaissement ne
              peut être enregistré maintenant. La réinscription, elle, reste possible — elle
              sera enregistrée <strong>impayée</strong>, et l'échéancier créé tel quel.
            </p>
            <p className="leading-relaxed mt-2">
              Pour encaisser dès la réinscription, ouvrez votre caisse depuis le portefeuille,
              puis revenez ici. Sinon, les frais s'encaisseront plus tard depuis le dossier
              de l'élève.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <p className="leading-relaxed">
              L'encaissement des frais relève des modules de gestion financière, non inclus
              dans la licence de l'établissement. L'échéancier de l'élève est néanmoins créé
              et conservé : il deviendra utilisable tel quel si la licence évolue.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <CreditCard size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Frais de reinscription</h3>
          </div>

          <div className="p-4 space-y-4">
            <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <p className="text-xs uppercase font-semibold text-[#246990]">Frais officiel</p>
                <p className="mt-1 text-lg font-bold text-gray-900">{formatAmount(effectiveFees.normalReinscription)}</p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-[#246990]">Reduction</p>
                <p className="mt-1 text-lg font-bold text-gray-900">-{formatAmount(effectiveFees.reductionInscription)}</p>
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-[#246990]">A payer</p>
                <p className="mt-1 text-lg font-bold text-gray-900">{formatAmount(effectiveFees.reinscription)}</p>
              </div>
            </div>

            <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50">
              <span>
                <span className="block text-sm font-semibold text-gray-900">Marquer les frais de reinscription comme payes</span>
                <span className="block text-xs text-gray-500">Le paiement sera enregistre directement avec la reinscription.</span>
              </span>
              <input
                type="checkbox"
                checked={Boolean(financeForm.reinscription?.paye)}
                onChange={(event) => setReinscriptionFeePaid(event.target.checked)}
                disabled={effectiveFees.reinscription <= 0}
                className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990] disabled:opacity-50"
              />
            </label>

            {/* Retour en cours de mois.
                Un eleve qui ne revient que le 15 n'a pas frequente le mois
                entier. Sans ce choix, la caisse encaissait la part juste et le
                mois restait marque « Partiel » - donc compte comme une dette qui
                n'existe pas. */}
            <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-800">
                  Cet élève reprend les cours à partir du
                </span>
                <input
                  type="date"
                  value={dateRetour}
                  onChange={(event) => setDateRetour(event.target.value)}
                  className="mt-1.5 h-9 w-full max-w-xs rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                />
                <span className="mt-1 block text-[11px] text-gray-600">
                  Laissez vide s'il reprend dès le début de l'année.
                </span>
              </label>

              {prorataRetour && (
                <div className="mt-3 border-t border-[#cfe3ec] pt-3">
                  <p className="text-sm font-medium text-gray-800">
                    Il reprend en cours de {prorataRetour.mois.nom} — {prorataRetour.joursPresents} jour
                    {prorataRetour.joursPresents > 1 ? 's' : ''} sur {prorataRetour.joursDuMois}. Que doit-il pour ce mois ?
                  </p>

                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                    <CarteChoix
                      actif={traitementMoisRetour === 'prorata'}
                      onClick={() => setTraitementMoisRetour('prorata')}
                      titre={`Le prorata — environ ${formatAmount(prorataRetour.montantProrata)}`}
                      description={`Au lieu de ${formatAmount(prorataRetour.montantPlein)}. Ce montant est intégralement dû : une fois réglé, le mois est soldé, pas partiel.`}
                    >
                      <CalculProrata
                        montantPlein={prorataRetour.montantPlein}
                        joursPresents={prorataRetour.joursPresents}
                        joursDuMois={prorataRetour.joursDuMois}
                        montantProrata={prorataRetour.montantProrata}
                      />
                    </CarteChoix>
                    <CarteChoix
                      actif={traitementMoisRetour === 'plein'}
                      onClick={() => setTraitementMoisRetour('plein')}
                      titre={`Le tarif normal — ${formatAmount(prorataRetour.montantPlein)}`}
                      description="Le mois est dû en entier, comme les autres."
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold text-gray-900">Mois scolaires</h4>
                  <p className="text-xs text-gray-500">Payez un mois ou plusieurs mois en avance si besoin.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => markAllMonths(false)} className="px-3 py-1.5 rounded-md border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                    Remettre a zero
                  </button>
                  <button type="button" onClick={() => markAllMonths(true)} className="px-3 py-1.5 rounded-md border border-[#b8d4e2] bg-[#e7f3f8] text-xs font-semibold text-[#246990] hover:bg-[#d9edf5]">
                    Tout payer
                  </button>
                </div>
              </div>

              {moisScolaires.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">Aucun mois scolaire disponible pour l'annee cible.</div>
              ) : (
                <div className="p-4 space-y-4">
                  {Object.entries(monthsByTrimester).map(([trimestre, months]) => (
                    <div key={trimestre} className="space-y-2">
                      <p className="text-xs font-bold uppercase text-[#246990]">{trimestre}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {months.map((mois) => {
                          const paiement = financeForm.paiements_mensuels?.[mois.id] || {};
                          // Le mois de reprise suit le choix fait au-dessus : sans
                          // cette lecture, la carte affichait le tarif plein et
                          // invitait à l'encaisser sur un mois proratisé.
                          const auProrata = Boolean(
                            prorataRetour
                            && traitementMoisRetour === 'prorata'
                            && String(prorataRetour.mois.id) === String(mois.id)
                          );
                          const montantDu = auProrata ? prorataRetour.montantProrata : effectiveFees.mensuel;
                          return (
                            <div
                              key={mois.id}
                              className={`rounded-lg border p-3 ${auProrata
                                ? 'border-[#cfe3ec] bg-[#f6fbfd]'
                                : 'border-gray-200 bg-gray-50'
                                }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-semibold text-gray-900">{mois.nom}</p>
                                <span className={`text-xs font-semibold ${auProrata ? 'text-[#246990]' : 'text-gray-500'}`}>
                                  {formatAmount(montantDu)}
                                </span>
                              </div>
                              {auProrata && (
                                <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#246990]">
                                  <Calculator className="h-3 w-3" />
                                  Prorata — {prorataRetour.joursPresents} jour
                                  {prorataRetour.joursPresents > 1 ? 's' : ''} sur {prorataRetour.joursDuMois},
                                  au lieu de {formatAmount(prorataRetour.montantPlein)}
                                </p>
                              )}
                              <input
                                type="number"
                                min="0"
                                max={montantDu}
                                value={paiement.montant_paye || 0}
                                onChange={(event) => updateMonthlyPayment(mois.id, event.target.value)}
                                className="mt-2 w-full px-3 py-2 rounded-md border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-bold text-gray-900">Resume comptable</h3>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total attendu</span>
              <span className="font-bold text-gray-900">{formatAmount(financeSummary.totalAttendu)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payé maintenant</span>
              <span className="font-bold text-[#246990]">{formatAmount(financeSummary.totalPaye)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Mois avances</span>
              <span className="font-bold text-gray-900">{financeSummary.moisPayes}</span>
            </div>
            <div className="rounded-lg bg-[#f4fafc] border border-[#cfe3ec] p-3 text-xs text-gray-600 leading-5">
              Les montants officiels restent recalcules par le serveur au moment de valider. Cette etape sert seulement a enregistrer les paiements immediats.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <ImageIcon size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Photo de l'élève</h3>
          </div>
          <div className="p-4">
            {dossier?.eleve?.photo_url ? (
              <div className="flex items-center gap-4">
                {/* Meme chemin relatif que partout ailleurs : la constante du composant,
                    pas une adresse rebatie ici -- c'est ainsi que l'ecart s'etait installe. */}
                <img
                  src={sourcePhotoUrl}
                  alt={`${dossier.eleve.prenom || ''} ${dossier.eleve.nom || ''}`.trim() || 'Élève'}
                  className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                />
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={keepPhoto}
                    onChange={(event) => setKeepPhoto(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">Conserver cette photo</span>
                    <span className="block text-xs text-gray-500">Elle sera reprise dans le nouveau dossier.</span>
                  </span>
                </label>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                Aucune photo n'était attachée au dossier source.
              </div>
            )}

            {photoPreview && (
              <div className="mt-4 rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-3">
                <p className="text-xs font-semibold text-[#246990] uppercase">Nouvelle photo</p>
                <div className="mt-3 relative mx-auto aspect-square w-full max-w-xs rounded-lg border border-gray-200 bg-white p-2">
                  <img src={photoPreview} alt="Nouvelle photo" className="h-full w-full object-cover rounded-md" />
                  <button type="button" onClick={clearCustomPhoto} className="absolute top-4 right-4 p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600">
                    <X size={16} />
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">Cette photo remplacera la photo source dans le nouveau dossier.</p>
              </div>
            )}

            {cameraActive && (
              <div className="mt-4 space-y-3">
                <div className="mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-lg bg-black border border-gray-200">
                  <video ref={videoRef} autoPlay playsInline className="h-full w-full object-cover" />
                </div>
                <p className="text-xs text-gray-500 text-center">Cadrez le visage au centre du carre avant de capturer.</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={capturePhoto} className="px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5]">
                    Capturer
                  </button>
                  <button type="button" onClick={stopPhotoCamera} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {cameraError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{cameraError}</div>
            )}

            {!cameraActive && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5]"
                >
                  <Upload size={16} />
                  Inserer
                </button>
                <button
                  type="button"
                  onClick={startPhotoCamera}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#b8d4e2] bg-[#e7f3f8] text-[#246990] text-sm font-semibold hover:bg-[#d9edf5]"
                >
                  <Camera size={16} />
                  Prendre une photo
                </button>
                {sourcePhotoUrl && !photoDataUrl && keepPhoto && (
                  <button
                    type="button"
                    onClick={() => setKeepPhoto(false)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                  >
                    Ne pas reprendre
                  </button>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoFileChange}
                  className="hidden"
                />
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <FileText size={18} className="text-[#246990]" />
            <h3 className="font-bold text-gray-900">Documents administratifs</h3>
          </div>
          <div className="p-4 space-y-3">
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <p className="text-sm font-semibold text-gray-900">{documentsTotal} document{documentsTotal > 1 ? 's' : ''} trouvé{documentsTotal > 1 ? 's' : ''}</p>
              <p className="text-xs text-gray-500 mt-1">Pièces d'identité, diplômes et autres documents du dossier source.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={copyDocuments}
                onChange={(event) => setCopyDocuments(event.target.checked)}
                disabled={documentsTotal === 0}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990] disabled:opacity-50"
              />
              <span>
                <span className="block text-sm font-semibold text-gray-900">Reprendre les documents</span>
                <span className="block text-xs text-gray-500">Les références des fichiers seront copiées dans le nouveau dossier.</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4fafc] p-4">
        <h3 className="font-bold text-[#246990]">Résumé avant validation</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Élève</p>
            <p className="font-bold text-gray-900">{studentForm.prenom} {studentForm.nom}</p>
          </div>
          <div>
            <p className="text-gray-500">Passage</p>
            <p className="font-bold text-gray-900">{selectedYear?.nom || 'Annee source'} -> {targetYear?.nom || 'Annee cible'}</p>
          </div>
          <div>
            <p className="text-gray-500">Classe</p>
            <p className="font-bold text-gray-900">{selectedClass?.nom || 'Non sélectionnée'}</p>
          </div>
          <div>
            <p className="text-gray-500">Famille</p>
            <p className="font-bold text-gray-900">{individualReductionMode === 'family' && resumeFamilyReductions && sourceFamily ? sourceFamily.nom : 'Non reprise'}</p>
          </div>
          <div>
            <p className="text-gray-500">Réduction</p>
            <p className="font-bold text-gray-900">
              {individualReductionMode === 'family'
                ? 'Famille'
                : individualReductionMode === 'exceptionnelle'
                  ? 'Exceptionnelle'
                  : individualReductionMode === 'politique'
                    ? "Politique d'école"
                    : 'Aucune'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Paiement immédiat</p>
            <p className="font-bold text-gray-900">{formatAmount(financeSummary.totalPaye)}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="animate-spin text-[#246990]" size={32} />
      </div>
    );
  }

  if (contextBlock) {
    return (
      <div className="w-full space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Année consultée" value={contextBlock.currentYear?.nom || activeYear?.nom || '-'} helper="Archive en lecture seule" icon={CalendarClock} tone="blue" />
          <StatCard label="Année active" value={contextBlock.activeYear?.nom || targetYear?.nom || '-'} helper="Point d'entrée des réinscriptions" icon={History} tone="teal" />
          <StatCard label="Réinscriptions" value="Bloquées" helper="Aucune action possible ici" icon={ShieldCheck} tone="slate" />
        </div>

        {renderReadOnlyYearBlock()}

        {renderToast()}
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">


      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Année source" value={(reinscriptionMode === 'pre_cloture' ? activeYear?.nom : selectedYear?.nom || previousYears?.[0]?.nom) || '-'} helper={reinscriptionMode === 'pre_cloture' ? 'Élèves à reconduire' : 'Année archivée à reprendre'} icon={CalendarClock} tone="blue" />
        <StatCard label="Année cible" value={targetYear?.nom || '-'} helper={targetYear?.preparation?.prete ? 'Classes et mois prêts' : 'Préparation requise'} icon={History} tone="teal" />
        <StatCard
          label="Réinscrits"
          value={progressReinscrits}
          helper={progressPartis > 0
            ? `Dont ${progressPartis} parti${progressPartis > 1 ? 's' : ''} depuis · ${stats.total || 0} opération(s)`
            : `${stats.total || 0} opération(s) enregistrée(s)`}
          icon={ShieldCheck}
          tone="slate"
        />
        {/* Le taux réel, hors bacheliers, s'affiche À CÔTÉ du taux actuel
            (services/reinscription/sortantsNaturels.js) : null tant qu'aucun
            résultat du BAC n'est saisi, et l'aide reste alors celle d'avant. */}
        <StatCard
          label="Taux réinscrit"
          value={`${progressRate}%`}
          helper={sourceProgress.taux_reinscription_reel !== null && sourceProgress.taux_reinscription_reel !== undefined
            ? `Hors ${sourceProgress.bacheliers} ${sourceProgress.examen_final && sourceProgress.examen_final !== 'BAC' ? `admis au ${sourceProgress.examen_final}` : `bachelier${sourceProgress.bacheliers > 1 ? 's' : ''}`} : ${sourceProgress.taux_reinscription_reel}% · ${progressPerdus} non réinscrit(s)`
            : `${progressPerdus} élève(s) non réinscrit(s)`}
          icon={Users}
          tone="clay"
        />
      </div>

      {previousYears.length === 0 || !targetReady ? (
        renderNoPreviousYear()
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white overflow-visible">
          <div className="px-6 py-5 border-b border-gray-100">
            <StepIndicator currentStep={currentStep} flowMode={flowMode} />
          </div>

          <div className="p-6 min-h-[420px] overflow-visible">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && (flowMode === 'family' ? renderFamilyStep2() : renderStep2())}
            {currentStep === 3 && (flowMode === 'family' ? renderFamilyStep3() : renderStep3())}
            {currentStep === 4 && (
              !comptabiliteOuverte
                ? renderStep4LectureSeule('licence')
                : !habiliteCaisse
                  ? renderStep4LectureSeule('habilitation')
                : !encaissementPossible
                  ? renderStep4LectureSeule('caisse')
                  : (flowMode === 'family' ? renderFamilyStep4() : renderStep4())
            )}
            {currentStep === 5 && (flowMode === 'family' ? renderFamilyStep5() : renderStep5())}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => (currentStep === 1 ? resetFlow() : setCurrentStep((step) => Math.max(1, step - 1)))}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
            >
              {currentStep === 1 ? 'Réinitialiser' : 'Précédent'}
            </button>

            <div className="flex items-center gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={resetFlow}
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                >
                  Annuler
                </button>
              )}

              {currentStep < 5 ? (
                <button
                  type="button"
                  disabled={
                    (currentStep === 1 && !canGoStep2) ||
                    (currentStep === 2 && !canGoStep3) ||
                    (currentStep === 3 && !canGoStep4) ||
                    (currentStep === 4 && !canGoStep5)
                  }
                  onClick={() => setCurrentStep((step) => Math.min(5, step + 1))}
                  className="px-5 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5] disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!canGoStep5 || submitting || bulkSubmitting}
                  onClick={flowMode === 'family' ? handleBulkFamilySubmit : handleSubmit}
                  className="px-5 py-2 rounded-lg bg-[#246990] text-white text-sm font-semibold hover:bg-[#3084b5] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {(submitting || bulkSubmitting) && <Loader2 size={16} className="animate-spin" />}
                  {flowMode === 'family' ? 'Valider la réinscription familiale' : 'Valider la réinscription'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {currentStep === 1 && (
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <History size={18} className="text-[#246990]" />
          <div>
            <h3 className="font-bold text-gray-900">Historique récent</h3>
            <p className="text-sm text-gray-500">Dernières réinscriptions enregistrées pour l'année active.</p>
          </div>
        </div>

        {history.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-500">Aucune réinscription enregistrée pour le moment.</div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3 text-left">Élève</th>
                  <th className="px-5 py-3 text-left">Années</th>
                  <th className="px-5 py-3 text-left">Classes</th>
                  <th className="px-5 py-3 text-left">Effectuée par</th>
                  <th className="px-5 py-3 text-right">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleHistory.map((log) => (
                  <tr key={log.id}>
                    <td className="px-5 py-3 font-semibold text-gray-900">{log.eleve_prenom} {log.eleve_nom}</td>
                    <td className="px-5 py-3 text-gray-600">{log.ancienne_annee || '-'} → {log.nouvelle_annee || '-'}</td>
                    <td className="px-5 py-3 text-gray-600">{log.ancienne_classe || '-'} → {log.nouvelle_classe || '-'}</td>
                    <td className="px-5 py-3 text-gray-600">{log.effectue_par_prenom} {log.effectue_par_nom}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{formatDateTime(log.date_reinscription)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {history.length > HISTORY_PAGE_SIZE && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {historyPageStart + 1}-{Math.min(historyPageStart + HISTORY_PAGE_SIZE, history.length)} sur {history.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={historySafePage <= 1}
                  onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
                  className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Precedent
                </button>
                <span className="text-xs font-semibold text-[#246990]">Page {historySafePage}/{historyTotalPages}</span>
                <button
                  type="button"
                  disabled={historySafePage >= historyTotalPages}
                  onClick={() => setHistoryPage((page) => Math.min(historyTotalPages, page + 1))}
                  className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
      )}

      {renderToast()}
    </div>
  );
};

export default ReInscriptionManager;
